/**
 * Registration Detail Page - Clara Vale V2
 *
 * 2-column card grid with per-card inline edit mode for admin users.
 * Edit requires a reason (audit trail). Permission-gated.
 */

import {
  useCallback,
  useMemo,
  useState,
  type KeyboardEvent,
  type ReactNode,
} from "react";

import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useParams, useNavigate } from "react-router";
import { useTranslation } from "react-i18next";
import {
  ArrowLeft,
  AlertTriangle,
  UserCircle,
  Package,
  Shield,
  Receipt,
  Settings2,
  FileText,
  ExternalLink,
  Plus,
  Mail,
  Copy,
  Printer,
  FileDown,
  History,
  RotateCcw,
  RefreshCw,
  ArrowUpRight,
  ArrowDownRight,
  Ban,
  Building2,
  Home,
  CalendarOff,
  ShieldOff,
  CheckCircle2,
  Tag,
  UserCog,
  MapPin,
  Link2,
  Trash2,
  ChevronDown,
  Search,
  Filter,
  Columns3,
  Download,
  Rows3,
  Grid3X3,
  Lock,
  Eye,
} from "lucide-react";
import { toast } from "sonner";
import { AppShell } from "@/admin/components/app-shell";
import { ConfigHelp } from "@/admin/components/config-help";
import { ContactCustomerDialog } from "@/admin/components/claims/contact-customer-dialog";
import {
  useEditHolding,
  useHolding,
  useHoldings,
} from "@/admin/hooks/use-products";
import {
  useEntityTypeByKey,
  useEntityTypes,
  type EntityAttributeDefinition,
} from "@/admin/hooks/use-entities";
import { useClaims } from "@/admin/hooks/use-claims";
import { usePermissions } from "@/admin/hooks/use-permissions";
import { useOrg } from "@/admin/hooks/use-org";
import { useFormatting } from "@/admin/hooks/use-formatting";
import {
  useFromClaim,
  useRegistrationBack,
  withFromClaim,
} from "@/admin/pages/registrations/registration-back";
import { sentenceCaseKey } from "@/shared/lib/humanise";
import {
  useRegistrationSettings,
  useCertificateDownloadEnabled,
} from "@/admin/hooks/use-registration-settings";
import api from "@/admin/lib/api";
import { canEntityPermission } from "@/admin/lib/entity-permissions";
import {
  formatEntityCardValue,
  registrationCardDisplayColumns,
  purchaseCardDisplayColumns,
} from "@/admin/lib/entity-card-display";
import {
  buildRegistrationConfiguredCardItems,
  buildRegistrationConfiguredSummaryItems,
} from "@/admin/lib/registration-configured-fields";
import { Button } from "@/shared/components/ui/button";
import { Input } from "@/shared/components/ui/input";
import { AccessLevelPanel } from "@/admin/components/access-level-panel";
import { AttachmentsCard } from "@/admin/components/shared/attachments-card";
import { NotesPanel } from "@/admin/components/shared/notes-panel";
import { RecordCommunicationsCard } from "@/admin/components/composer/record-communications-card";
import { MentionedText } from "@/shared/components/ui/mention-textarea";
import { StatusBadge } from "@/admin/components/shared/status-badge";
import { defineField, formatPhoneDisplay } from "@/shared/lib/forms";
import type { CardFieldItem } from "@/admin/components/shared/editable-card-types";
import type {
  Holding,
  HoldingPendingException,
  HoldingWarranty,
  PendingWarrantySummary,
  WorkflowIssueSeverity,
} from "@/admin/types/products";
import { DetailCard } from "@/admin/components/shared/detail-card";
import { RegistrationResolutionWorkspace } from "@/admin/components/workflow-issues/registration-resolution-workspace";
import { EditableSummaryDetailCard } from "@/admin/components/shared/editable-summary-detail-card";
import { IdentifyProductDialog } from "@/admin/components/identify-product-dialog";
import { useFeatureFlag, FLAGS } from "@/admin/hooks/use-feature-flag";
import { EditFormDialog } from "@/admin/components/shared/edit-form-dialog";
import {
  CardActionMenu,
  type CardActionMenuItem,
} from "@/admin/components/shared/card-action-menu";
import { EntityActionMenu } from "@/admin/components/shared/entity-action-menu";
import {
  EDIT_HOST_ACTION_KEY,
  EntityCardActions,
} from "@/admin/components/shared/entity-card-actions";
import { DemoConfirmModal } from "@/admin/components/shared/demo-confirm-modal";
import { RegistrationWarrantyRecordPage } from "@/admin/pages/registrations/registration-warranty-record-page";
import {
  WorkflowIssueResolveModal,
  type WorkflowIssuePreview,
} from "@/admin/components/workflow-issues/workflow-issue-resolve-modal";
import { WorkflowIssueActionButton } from "@/admin/components/shared/workflow-issue-action-button";
import { SYSTEM_EXCEPTION_REASON_OPTIONS } from "@/admin/constants/workflow-exception-reasons";
import type {
  WorkflowIssueContext,
  WorkflowActionLog,
} from "@/shared/lib/workflows/types";
import type { StructureSummary } from "@/admin/types/hosts";
import { holdingIdentityField } from "@/admin/pages/claims/holding-serial";
import { identityAttributeCodes } from "@/admin/components/registration-wizard/stock-resolved-product";
import {
  getStructureDisplay,
  humaniseDetailValue,
  isOtherStructureValue,
  titleCaseStructureLabel,
} from "./get-structure-display";
import {
  SummaryDetailCard,
  SummaryDetailGrid,
  SummaryDetailItem,
} from "@/admin/components/shared/summary-detail-card";

import { toastApiError } from "@/shared/lib/api-error";
import {
  EntityHistoryCard,
  WorkflowHistoryCard,
  WorkflowStateBadge,
} from "@/shared/components/workflows";
import {
  useWorkflowInstanceLookup,
  useWorkflowHistory,
  useEntityLog,
} from "@/shared/hooks/use-workflows";
import type { FieldType, FormFieldMeta } from "@/shared/lib/forms/types";
import {
  addressDisplay,
  isAddressLike,
  toHostAddressColumns,
} from "@/shared/lib/formatting";
import { resolveHoldingConsumerFields } from "@/admin/lib/customer-card";
import {
  formatClaimId,
  formatDurationFromMonths,
  formatRegistrationReference,
} from "@/admin/lib/formatting";
import { cn } from "@/shared/lib/utils";
import { plannedWarranty } from "@/admin/lib/warranty-record";

const PAYMENT_WORKFLOW_KEY = "lifecycle_payment";
const STATUS_WORKFLOW_KEY = "lifecycle_status";

const INTERNAL_CAPTURED_VALUE_KEYS = new Set([
  "issue_uuid",
  "predicate_key",
  "issue_kind",
  "correlation_id",
  "workflow_instance",
  "workflow_action",
  "rule_uuid",
  "binding_uuid",
]);

const WARRANTY_TYPE_LABELS: Record<string, string> = {
  MANUFACTURER_WARRANTY: "Standard",
  RETAILER_WARRANTY: "Retailer",
  EXTENDED_WARRANTY: "Extended",
};

const COVERAGE_LABELS: Record<string, string> = {
  MATERIAL_ONLY: "Materials Only",
  LABOR_ONLY: "Labor Only",
  MATERIAL_AND_LABOR: "Materials & Labor",
};

/** Attribute groups for organising into cards */
const VEHICLE_FIELDS = new Set([
  "make",
  "model",
  "colour",
  "vehicle_number",
  "vin",
  "vehicle_type",
  "new_vehicle",
]);
const PURCHASE_FIELDS = new Set([
  "purchase_date",
  "price",
  "sales_person",
  "reference",
  "purchase_price",
  "sales_reference",
]);

function formatAttrValue(value: unknown): string {
  return formatEntityCardValue(value);
}

function deriveStatus(
  holdingStatus: string | undefined,
  pendingExceptionCount: number,
  warranties: HoldingWarranty[],
): string {
  if (holdingStatus === "cancelled") return "Cancelled";
  // Lifecycle wins: a draft holding is never "Active", regardless of
  // whether a warranty row exists. Pending approval is shown explicitly
  // so reviewers don't confuse it with a stalled draft.
  if (holdingStatus === "draft") {
    return pendingExceptionCount > 0 ? "Pending Approval" : "Draft";
  }
  if (holdingStatus === "transferred") return "Transferred";

  if (warranties.length === 0) return "Registered";
  if (
    warranties.some(
      (w) => w.status === "ACTIVE" || w.status === "WarrantyStatus.Active",
    )
  )
    return "Active";
  if (
    warranties.every(
      (w) => w.status === "EXPIRED" || w.status === "WarrantyStatus.Expired",
    )
  )
    return "Expired";
  return "Registered";
}

function pickWorkflowState(
  states: Array<{
    instance_uuid: string;
    workflow_key: string;
    workflow_label: string;
    owns_resource_lifecycle?: boolean;
    state_key: string;
    state_label: string;
    state_color: string;
    is_terminal: boolean;
  }>,
  workflowKey: string,
) {
  return states.find((state) => state.workflow_key === workflowKey) ?? null;
}

function entityDisplayAttributes(
  attributes: EntityAttributeDefinition[] | undefined,
  surface: "table" | "detail",
) {
  return (attributes ?? []).filter((attribute) => {
    const config = attribute.configuration_data ?? {};
    const isWorkflowState =
      config.value_source === "workflow_state" &&
      Boolean(config.workflow_key || attribute.code_name);
    const isRelationship =
      config.value_source === "entity_relationship" &&
      Boolean(config.relationship_key || attribute.code_name);
    return (
      (isWorkflowState || isRelationship) && Boolean(config.display?.[surface])
    );
  });
}

function formatRelationshipValue(holding: Holding, relationshipKey: string) {
  switch (relationshipKey) {
    case "consumer": {
      const consumer = holding.consumer_account;
      const name = [consumer.first_name, consumer.last_name]
        .filter(Boolean)
        .join(" ");
      return name || consumer.email || "";
    }
    case "organisation":
      return (
        holding.organisation?.name ||
        holding.partnership?.downstream_organisation_name ||
        ""
      );
    case "product":
      return (
        holding.product.cached_short_name ||
        holding.product.cached_long_name ||
        ""
      );
    case "warranty":
    case "warranties": {
      const count = holding.warranties?.length ?? 0;
      if (count > 0) {
        return `${count} warrant${count === 1 ? "y" : "ies"}`;
      }
      return holding.pending_warranty_summary?.option_display_name || "";
    }
    default:
      return "";
  }
}

function safeProductDisplayText(value: unknown): string {
  if (value === null || value === undefined) return "";
  if (typeof value !== "string") {
    return addressDisplay(value) ? "" : String(value).trim();
  }

  const trimmed = value.trim();
  if (!trimmed) return "";
  if (addressDisplay(trimmed) && trimmed.startsWith("{")) return "";
  if (
    (trimmed.startsWith("{") && trimmed.endsWith("}")) ||
    (trimmed.startsWith("[") && trimmed.endsWith("]"))
  ) {
    return "";
  }
  return trimmed;
}

function getConfiguredStatusWorkflowKey(
  attributes: EntityAttributeDefinition[] | undefined,
) {
  const statusAttribute = (attributes ?? []).find(
    (attribute) => attribute.code_name === "status",
  );
  if (
    statusAttribute?.configuration_data?.value_source === "workflow_state" &&
    statusAttribute.configuration_data.workflow_key
  ) {
    return statusAttribute.configuration_data.workflow_key;
  }
  return STATUS_WORKFLOW_KEY;
}

type WorkflowStateRef = {
  instance_uuid: string;
  workflow_key: string;
  workflow_label: string;
  owns_resource_lifecycle?: boolean;
  state_key: string;
  state_label: string;
  state_color: string;
  is_terminal: boolean;
};

export function RegistrationDetailRouterPage() {
  const { t } = useTranslation();
  const back = useRegistrationBack();
  const { uuid } = useParams<{ uuid: string }>();
  const { data: holding, isLoading } = useHolding(uuid ?? null);
  const { data: holdingEntity } = useEntityTypeByKey("holding");
  const { isPlaceholderData: registrationSettingsPlaceholder } =
    useRegistrationSettings();
  const workflowStates =
    (holding as { workflow_states?: WorkflowStateRef[] } | undefined)
      ?.workflow_states ?? [];
  const statusWorkflowKey = getConfiguredStatusWorkflowKey(
    holdingEntity?.attributes,
  );
  const statusWorkflowState = pickWorkflowState(
    workflowStates,
    statusWorkflowKey,
  );

  if (isLoading || registrationSettingsPlaceholder) {
    return (
      <AppShell>
        <main className="page-container py-10">
          <p className="py-20 text-center text-[13px] text-[var(--ink-3)]">
            {t("common.loading")}
          </p>
        </main>
      </AppShell>
    );
  }

  if (!holding) {
    return (
      <AppShell>
        <main className="page-container py-10">
          <p className="py-20 text-center text-[13px] text-[var(--ink-3)]">
            {t("registrations.notFound")}
          </p>
        </main>
      </AppShell>
    );
  }

  // Registration record views collapse to two surfaces, chosen by data, not
  // by tenant:
  //   * PROPERTY host  -> the bespoke enterprise property record (OC).
  //   * everything else -> the single, config-driven registration record
  //     (``RegistrationWarrantyRecordPage``): REG-prefixed title, a workflow
  //     signal card when a workflow is attached, the config-driven detail
  //     cards, and the activity log last. It renders the host card only when a
  //     host is present, so it serves both host tenants (vehicle/vessel) and
  //     host-less Standard SaaS tenants from one surface. The legacy
  //     ``registration_detail_view`` split (vehicle vs the editable-card
  //     Standard layout) has been folded into this one view.
  if (holding.host?.kind === "PROPERTY") {
    const structureTypeAttr = holding.host?.attribute_values?.find(
      (attr) =>
        attr.code_name === "structure_type" ||
        attr.code_name === "property_type",
    );
    const structureType = humaniseDetailValue(structureTypeAttr?.value);
    const status =
      holding.status === "transferred"
        ? "Transferred"
        : statusWorkflowState?.state_label ||
          deriveStatus(
            holding.status,
            holding.pending_exceptions?.length ?? 0,
            holding.warranties ?? [],
          );

    return (
      <AppShell>
        <PropertyRegistrationRecordV4
          holding={holding}
          status={status}
          structureType={structureType}
          onBack={back.go}
          backLabel={back.label}
        />
      </AppShell>
    );
  }

  return <RegistrationWarrantyRecordPage />;
}

export function RegistrationDetailPage() {
  const { t } = useTranslation();
  const back = useRegistrationBack();
  const { uuid } = useParams<{ uuid: string }>();
  const navigate = useNavigate();
  const { data: holding, isLoading } = useHolding(uuid ?? null);
  const { data: holdingEntity } = useEntityTypeByKey("holding");
  const { data: hostEntity } = useEntityTypeByKey("host");
  const { data: warrantyEntity } = useEntityTypeByKey("warranty");
  // This card names the warranty entity, so its heading is the entity's own
  // label - Supagard's reads "GUARANTEE". The translated string is the
  // fallback for a tenant with no registry row.
  const warrantyNounLabel =
    warrantyEntity?.label ?? t("registrationDetail.guarantee", "Warranty");
  const { data: entityTypes = [] } = useEntityTypes();
  const { can } = usePermissions();
  const hasProductIdentification = useFeatureFlag(FLAGS.PRODUCT_IDENTIFICATION);
  const [identifyProductOpen, setIdentifyProductOpen] = useState(false);
  const { orgUuid } = useOrg();
  const certificateDownloadEnabled = useCertificateDownloadEnabled();
  const { formatCurrency, formatDate, locale, currency } = useFormatting();
  const editHolding = useEditHolding();
  const claimsParams = useMemo(
    () => (uuid ? { holding_uuid: uuid } : undefined),
    [uuid],
  );
  const {
    data: claimsData,
    isPending: claimsPending,
    isFetching: claimsFetching,
  } = useClaims(claimsParams ?? undefined);
  const [contactCustomerOpen, setContactCustomerOpen] = useState(false);

  // Workflow state for the holding's primary lifecycle (currently the
  // OC extended-warranty payment flow). Hooks must run unconditionally,
  // so derive state defensively before any early returns below.
  const workflowStates = useMemo(
    () =>
      (
        holding as unknown as
          | {
              workflow_states?: WorkflowStateRef[];
            }
          | undefined
      )?.workflow_states ?? [],
    [holding],
  );
  const statusWorkflowKey = getConfiguredStatusWorkflowKey(
    holdingEntity?.attributes,
  );
  const paymentWorkflowState = pickWorkflowState(
    workflowStates,
    PAYMENT_WORKFLOW_KEY,
  );
  const statusWorkflowState = pickWorkflowState(
    workflowStates,
    statusWorkflowKey,
  );
  // Fallback lookup when the holding payload didn't include
  // workflow_states yet (e.g. stale cache). Disabled once we already
  // have the embedded rows.
  const workflowLookup = useWorkflowInstanceLookup(
    paymentWorkflowState || statusWorkflowState ? null : "holding",
    paymentWorkflowState || statusWorkflowState ? null : (uuid ?? null),
  );
  const paymentWorkflowInstanceUuid =
    paymentWorkflowState?.instance_uuid ??
    (workflowLookup.data?.workflow_key === PAYMENT_WORKFLOW_KEY
      ? workflowLookup.data.uuid
      : null);
  const statusWorkflowInstanceUuid =
    statusWorkflowState?.instance_uuid ??
    (workflowLookup.data?.workflow_key === STATUS_WORKFLOW_KEY
      ? workflowLookup.data.uuid
      : null);
  const workflowHistoryStates = useMemo(
    () =>
      workflowStates.filter(
        (state, index, allStates) =>
          Boolean(state.instance_uuid) &&
          allStates.findIndex(
            (candidate) => candidate.instance_uuid === state.instance_uuid,
          ) === index,
      ),
    [workflowStates],
  );
  const primaryWorkflowHistoryInstanceUuid =
    statusWorkflowInstanceUuid ??
    paymentWorkflowInstanceUuid ??
    workflowHistoryStates[0]?.instance_uuid ??
    null;
  const canEdit = can("holdings.edit.global");

  // ── Demo confirm modal ──────────────────────────────────────────────
  // Destructive primary/menu buttons (cancel terms, downgrade tier,
  // resync to cart, …) aren't wired to the backend yet, so they go
  // through a small confirm dialog that captures a reason and toasts
  // on success. Replace the toasts with real mutations as those
  // workflows ship.
  type ConfirmConfig = Omit<
    React.ComponentProps<typeof DemoConfirmModal>,
    "open" | "onOpenChange"
  >;

  const [confirmConfig, setConfirmConfig] = useState<ConfirmConfig | null>(
    null,
  );

  const openConfirm = (config: ConfirmConfig) => setConfirmConfig(config);

  // The preview-mode exception helper lives further down the body (it
  // depends on `host`/`customerName`/`productTitle` derivations).
  const [previewConfig, setPreviewConfig] =
    useState<WorkflowIssuePreview | null>(null);

  const notifyComingSoon = (label: string) =>
    toast.message(`${label} - coming soon`, {
      description:
        "We've recorded the exception in this session. The mutation will run once the backend workflow is wired up.",
    });
  // ────────────────────────────────────────────────────────────────────

  // Best-effort clipboard helper: works in modern browsers and falls back
  // gracefully when navigator.clipboard isn't available (e.g. http on iOS
  // Safari). Always emits a toast so the user gets feedback either way.
  const copyToClipboard = async (value: string, label = "Copied") => {
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(value);
      } else {
        const textarea = document.createElement("textarea");
        textarea.value = value;
        textarea.style.position = "fixed";
        textarea.style.opacity = "0";
        document.body.appendChild(textarea);
        textarea.select();
        document.execCommand("copy");
        textarea.remove();
      }
      toast.success(label);
    } catch {
      toast.error("Couldn't copy to clipboard");
    }
  };

  const handleCustomerSave = async (
    values: Record<string, unknown>,
    reason?: string,
  ) => {
    try {
      await editHolding.mutateAsync({
        uuid: uuid!,
        edit_reason: reason ?? "",
        consumer_fields: values as Record<string, string>,
      });
      toast.success("Registration updated");
    } catch (err) {
      toastApiError(err as Error, "Failed to update registration");
      throw err;
    }
  };

  const handleAdditionalDetailsSave = async (
    values: Record<string, unknown>,
    reason?: string,
  ) => {
    try {
      await editHolding.mutateAsync({
        uuid: uuid!,
        edit_reason: reason ?? "",
        attribute_values: values,
      });
      toast.success("Registration updated");
    } catch (err) {
      toastApiError(err as Error, "Failed to update registration");
      throw err;
    }
  };

  if (isLoading) {
    return (
      <AppShell>
        <main className="page-container py-10">
          <p className="py-20 text-center text-[13px] text-[var(--ink-3)]">
            {t("common.loading")}
          </p>
        </main>
      </AppShell>
    );
  }

  if (!holding) {
    return (
      <AppShell>
        <main className="page-container py-10">
          <p className="py-20 text-center text-[13px] text-[var(--ink-3)]">
            {t("registrations.notFound")}
          </p>
        </main>
      </AppShell>
    );
  }

  // Draft registrations land on the full-page Resolution workspace
  // instead of the read-only detail layout. "Draft" is the seeded
  // ``lifecycle_status`` workflow state (the holding row's own
  // ``status`` stays ``registered`` until the workflow promotes it),
  // so we key off the workflow state rather than the raw DB column.
  // Non-draft registrations still get the regular detail page.
  const isDraft =
    statusWorkflowState?.state_key === "draft" || holding.status === "draft";
  if (isDraft) {
    return (
      <AppShell>
        <DraftResolutionPage
          holdingUuid={holding.uuid}
          productLabel={
            holding.product?.cached_short_name ||
            holding.product?.cached_long_name ||
            "registration"
          }
          canApprove={
            can("exceptions.approve") || can("registrations.exceptions.approve")
          }
          onBack={back.go}
        />
      </AppShell>
    );
  }

  const consumer = holding.consumer_account;
  const consumerFields = resolveHoldingConsumerFields(holding);
  const product = holding.product;
  const attributes = holding.attribute_values ?? [];
  const warranties = holding.warranties ?? [];
  const pendingExceptions = holding.pending_exceptions ?? [];
  const status =
    holding.status === "transferred"
      ? "Transferred"
      : statusWorkflowState?.state_label ||
        deriveStatus(holding.status, pendingExceptions.length, warranties);

  const host = holding.host;
  const hostKind = host?.kind ?? null;
  const hostTemplate = host?.host_template ?? null;
  // The API serialises the host address as the canonical {display, value}
  // envelope - the flat `host.address_*` columns are not on the wire. Read
  // them through this bridge so an edited address actually shows (WPS-155).
  const hostAddress = toHostAddressColumns(host?.address);

  // Holding-level attribute values split into groups
  const purchaseAttrs = attributes.filter((a) =>
    PURCHASE_FIELDS.has(a.code_name),
  );
  const additionalAttrs = attributes.filter(
    (a) =>
      !VEHICLE_FIELDS.has(a.code_name) && !PURCHASE_FIELDS.has(a.code_name),
  );

  const customerName =
    [consumer.first_name, consumer.last_name].filter(Boolean).join(" ") ||
    "Customer";
  const customerSubtitle =
    consumer.email || consumer.phone_number || "Registration contact details";
  const productTitle =
    safeProductDisplayText(product.cached_short_name) ||
    safeProductDisplayText(product.cached_long_name) ||
    product.product_template?.name ||
    holding.product_category ||
    "Product";
  const productType = product.product_type
    ? product.product_type.charAt(0).toUpperCase() +
      product.product_type.slice(1).toLowerCase()
    : undefined;
  const productSubtitle =
    product.product_template?.name ||
    productType ||
    "Product registration details";
  const contactClaim = claimsData?.results?.[0] ?? null;

  // ── Shared subject context for preview-mode exception modals ──────
  // Mirrors the backend `WorkflowIssueContext` shape so the preview
  // header reads identically to a real-issue header. The trigger panel
  // is per-action; callers fill it via `openExceptionPreview` below.
  const hostExceptionContext: Omit<WorkflowIssueContext, "trigger"> = (() => {
    const kind =
      hostKind === "PROPERTY"
        ? "PROPERTY"
        : hostKind === "VESSEL"
          ? "VESSEL"
          : hostKind === "VEHICLE"
            ? "VEHICLE"
            : "REGISTRATION";

    let subjectTitle = "-";
    let subjectSubtitle = "";

    if (host) {
      if (hostKind === "PROPERTY") {
        const locationLine = addressDisplay({
          address_town_or_city: hostAddress.address_town_or_city,
          address_postcode: hostAddress.address_postcode,
          address_country: hostAddress.address_country,
        });
        // Address first, matching `addressTitle` and `hostTitle` below: a
        // property host's `name` is composed from the address once at creation
        // and never recomputed, so preferring it here would keep showing the
        // pre-edit address in the exception preview - the WPS-155 symptom, just
        // in a different surface.
        subjectTitle =
          hostAddress.address_line_1 || host.name || locationLine || "-";
        subjectSubtitle = locationLine;
      } else {
        subjectTitle = host.name || host.registration_number || "-";
        subjectSubtitle = host.vin
          ? `${hostKind === "VESSEL" ? "HIN" : "VIN"} ${host.vin}`
          : "";
      }
    }

    const ownerSecondary = holding.activated_at
      ? `Since ${new Date(holding.activated_at).toLocaleDateString(locale, {
          day: "numeric",
          month: "short",
          year: "numeric",
        })}`
      : holding.created_at
        ? `Since ${new Date(holding.created_at).toLocaleDateString(locale, {
            day: "numeric",
            month: "short",
            year: "numeric",
          })}`
        : undefined;

    return {
      subject: {
        kind,
        title: subjectTitle,
        subtitle: subjectSubtitle,
      },
      meta_sections: [
        {
          label: "OWNER",
          primary: customerName,
          secondary: ownerSecondary,
        },
        {
          label: "PRODUCT",
          primary: productTitle || "-",
          secondary: productSubtitle,
        },
      ],
    };
  })();

  /**
   * Open the canonical resolve modal in preview mode for an action
   * that hasn't been wired to a backend workflow yet. The caller
   * supplies the action-specific rule trigger and the reason code to
   * pre-select; the rest (subject, meta sections, full reason
   * catalog) comes from `hostExceptionContext` and the shared
   * `SYSTEM_EXCEPTION_REASON_OPTIONS` so operators always see the familiar
   * header and can re-pick the exception code if the suggestion
   * doesn't quite fit.
   */
  const openExceptionPreview = (config: {
    triggerTitle: string;
    triggerDescription: string;
    defaultReasonCode: string;
    confirmLabel: string;
    successLabel: string;
  }) => {
    setPreviewConfig({
      context: {
        ...hostExceptionContext,
        trigger: {
          title: config.triggerTitle,
          description: config.triggerDescription,
          breakdown: [],
        },
      },
      reasonOptions: SYSTEM_EXCEPTION_REASON_OPTIONS,
      defaultReasonCode: config.defaultReasonCode,
      confirmLabel: config.confirmLabel,
      onConfirm: () => {
        notifyComingSoon(config.successLabel);
      },
    });
  };

  const defaultProductItems: CardFieldItem[] = [
    {
      field: defineField({
        code_name: "type",
        display_name: "Type",
        field_type: "TEXT",
      }),
      value: productType,
    },
    {
      field: defineField({
        code_name: "category",
        display_name: "Category",
        field_type: "TEXT",
      }),
      value: holding.product_category || product.category?.name || undefined,
    },
    {
      field: defineField({
        code_name: "_view_product",
        display_name: "View",
        field_type: "TEXT",
      }),
      value: "View product",
      readOnly: true,
      link: true,
      onLinkClick: () => navigate(`/manage/products/${product.uuid}`),
    },
  ];
  const additionalDetailItems: CardFieldItem[] = additionalAttrs.map(
    (attr) => ({
      field: defineField({
        code_name: attr.code_name,
        display_name: attr.display_name,
        field_type: attr.field_type ?? "TEXT",
      }),
      value: attr.value,
      displayValue: formatAttrValue(attr.value),
    }),
  );
  // Columns configured via the product template Display tab.
  const configuredProductCardItems: CardFieldItem[] =
    buildRegistrationConfiguredCardItems({
      holding,
      columns: registrationCardDisplayColumns(holdingEntity).filter(
        (column) => !column.id.startsWith("transaction_attribute:"),
      ),
      statusWorkflowKey,
      entityTypes,
      locale,
      currency,
    });
  const productItems =
    configuredProductCardItems.length > 0
      ? configuredProductCardItems
      : defaultProductItems;

  // Host display columns can still enrich the registration details card, but
  // product template columns belong in the Product card above.
  const configuredAdditionalDetailItems: CardFieldItem[] =
    buildRegistrationConfiguredCardItems({
      holding,
      columns: registrationCardDisplayColumns(hostEntity),
      statusWorkflowKey,
      entityTypes,
      locale,
      currency,
    });

  // Columns for the purchase / transaction card, gated by the fields the
  // registration step actually collected (see purchaseCardDisplayColumns).
  const purchaseCardColumns = purchaseCardDisplayColumns(
    holdingEntity,
    holding.collected_transaction_attribute_codes,
  );
  const configuredPurchaseCardItems: CardFieldItem[] =
    buildRegistrationConfiguredCardItems({
      holding,
      columns: purchaseCardColumns,
      statusWorkflowKey,
      entityTypes,
      locale,
      currency,
    });
  // The warranty registration_card columns are tenant-wide - they span every
  // warranty attribute across every template. For a specific registered
  // warranty, attributes that belong to other templates resolve empty and are
  // flagged `muted`. Drop them (`!item.muted`) so this card shows only the
  // attributes that actually apply to this warranty, matching the dedicated
  // warranty record pages and the product/purchase cards (which already hide
  // empty rows via buildRegistrationConfiguredCardItems). Without this the card
  // renders inapplicable attributes as blank "-" rows.
  const configuredWarrantySummaryItems =
    buildRegistrationConfiguredSummaryItems({
      holding,
      columns: registrationCardDisplayColumns(warrantyEntity),
      statusWorkflowKey,
      entityTypes,
      locale,
      currency,
    }).filter((item) => !item.muted);
  const entityWorkflowDetailItems: CardFieldItem[] = entityDisplayAttributes(
    holdingEntity?.attributes,
    "detail",
  ).map((attr) => {
    if (attr.configuration_data.value_source === "entity_relationship") {
      const value = formatRelationshipValue(
        holding,
        attr.configuration_data.relationship_key || attr.code_name,
      );
      return {
        field: defineField({
          code_name: attr.code_name,
          display_name:
            attr.configuration_data.display?.label || attr.display_name,
          field_type: "TEXT",
        }),
        value,
        displayValue: value || "-",
        readOnly: true,
      };
    }
    const workflowKey = attr.configuration_data.workflow_key || attr.code_name;
    const state = pickWorkflowState(workflowStates, workflowKey);
    return {
      field: defineField({
        code_name: attr.code_name,
        display_name:
          attr.configuration_data.display?.label || attr.display_name,
        field_type: "TEXT",
      }),
      value: state?.state_label || undefined,
      displayValue: state ? (
        <WorkflowStateBadge
          workflowKey={state.workflow_key}
          stateKey={state.state_key}
          label={state.state_label || state.state_key}
          color={state.state_color}
          isTerminal={state.is_terminal}
        />
      ) : (
        "-"
      ),
      readOnly: true,
    };
  });
  const allAdditionalDetailItems =
    configuredAdditionalDetailItems.length > 0
      ? configuredAdditionalDetailItems
      : [...entityWorkflowDetailItems, ...additionalDetailItems];
  const statusExceptions = pendingExceptions.filter((exc) => {
    if (exc.workflow_key === STATUS_WORKFLOW_KEY) return true;
    if (exc.workflow_key && exc.workflow_key !== STATUS_WORKFLOW_KEY)
      return false;
    return exc.issue_kind === "approval_required";
  });
  const pendingWarranty = holding.pending_warranty_summary ?? null;
  const holdingStatusKey = holding.status?.toLowerCase() ?? null;
  const statusWorkflowStateKey =
    statusWorkflowState?.state_key?.toLowerCase() ?? null;
  const isRegistrationActive =
    statusWorkflowStateKey === "active" ||
    statusWorkflowStateKey === "registered" ||
    (!statusWorkflowState &&
      (holdingStatusKey === "active" || holdingStatusKey === "registered"));
  const isSyntheticPendingWarranty =
    warranties.length === 0 && Boolean(pendingWarranty);
  const activeSyntheticWarranty =
    isSyntheticPendingWarranty &&
    isRegistrationActive &&
    statusExceptions.length === 0;
  const effectiveWarranties =
    warranties.length > 0 || !pendingWarranty
      ? warranties
      : [
          activeSyntheticWarranty
            ? {
                ...pendingWarranty,
                option_display_name:
                  pendingWarranty.option_display_name?.toLowerCase().trim() ===
                  "pending warranty"
                    ? ""
                    : pendingWarranty.option_display_name,
                status: "Registered",
              }
            : pendingWarranty,
        ];
  const purchaseItems: CardFieldItem[] = [];
  if (configuredPurchaseCardItems.length > 0) {
    purchaseItems.push(...configuredPurchaseCardItems);
  } else if (warranties.length > 0 && warranties[0].active_from) {
    purchaseItems.push({
      field: defineField({
        code_name: "purchase_date",
        display_name: "Date",
        field_type: "DATE",
      }),
      value: warranties[0].active_from,
    });
  }
  if (
    configuredPurchaseCardItems.length === 0 &&
    holding.transaction_attribute_values &&
    holding.transaction_attribute_values.length > 0
  ) {
    holding.transaction_attribute_values.forEach((tav) => {
      purchaseItems.push({
        field: defineField({
          code_name: tav.code_name,
          display_name: tav.display_name,
          field_type: "TEXT",
        }),
        value: tav.value,
        displayValue: formatAttrValue(tav.value),
      });
    });
  } else if (configuredPurchaseCardItems.length === 0) {
    purchaseItems.push(
      {
        field: defineField({
          code_name: "price",
          display_name: "Price",
          field_type: "MONEY",
        }),
        value: holding.purchase_price,
      },
      {
        field: defineField({
          code_name: "sales_person",
          display_name: "Sales Person",
          field_type: "TEXT",
        }),
        value: holding.sales_person || undefined,
      },
      {
        field: defineField({
          code_name: "reference",
          display_name: "Reference",
          field_type: "TEXT",
        }),
        value: holding.sales_reference || undefined,
      },
      ...purchaseAttrs.map((attr) => ({
        field: defineField({
          code_name: attr.code_name,
          display_name: attr.display_name,
          field_type: "TEXT",
        }),
        value: attr.value,
        displayValue: formatAttrValue(attr.value),
      })),
    );
  }
  if (configuredPurchaseCardItems.length === 0) {
    purchaseItems.push({
      field: defineField({
        code_name: "dealer",
        display_name: "Dealer",
        field_type: "TEXT",
      }),
      value:
        holding.partnership?.downstream_organisation_name ||
        holding.organisation?.name ||
        undefined,
    });
  }
  const structureTypeAttr = host?.attribute_values?.find(
    (attr) =>
      attr.code_name === "structure_type" || attr.code_name === "property_type",
  );
  const structureType = humaniseDetailValue(structureTypeAttr?.value);

  // Severity ordering mirrors :class:`apps.rules.choices.Severity`
  // (BLOCK > WARN > INFO); a missing severity falls back to "blocking"
  // because an open issue still gates progress.
  const severityRank: Record<WorkflowIssueSeverity, number> = {
    BLOCK: 3,
    WARN: 2,
    INFO: 1,
  };
  const severityToLevel = (
    severity: WorkflowIssueSeverity | null | undefined,
  ): "error" | "warning" | "info" => {
    switch (severity) {
      case "WARN":
        return "warning";
      case "INFO":
        return "info";
      case "BLOCK":
      default:
        return "error";
    }
  };
  // Pick the highest-severity message-bearing exception from a slice of
  // the pending exception list. Used to drive both the warranty card
  // (all blockers, draft framing) and the purchase card (payment-only
  // blockers).
  const pickLeadException = (
    exceptions: typeof pendingExceptions,
  ): (typeof exceptions)[number] | undefined =>
    [...exceptions]
      .filter((exc) => exc.message)
      .sort(
        (a, b) =>
          (severityRank[(b.severity ?? "BLOCK") as WorkflowIssueSeverity] ??
            0) -
          (severityRank[(a.severity ?? "BLOCK") as WorkflowIssueSeverity] ?? 0),
      )[0];

  // Bind an alert action to a specific WorkflowIssue. The button
  // label IS the resolving workflow action's name (e.g.
  // ``mark_paid`` → "Mark as paid"), pulled from the issue payload
  // so it works even when ``visible-actions`` returns empty (which
  // happens whenever an open issue is gating transitions). Click
  // opens the canonical resolve modal - that modal handles the
  // grant-exception / remediate / dismiss split.
  const buildIssueAction = (
    leadException: HoldingPendingException | undefined,
    level: "error" | "warning" | "info",
    workflowKey: string | undefined,
  ) =>
    leadException ? (
      <WorkflowIssueActionButton
        issueUuid={leadException.uuid}
        resolvingActionKey={leadException.resolving_action_key}
        resolvingActionLabel={leadException.resolving_action_label}
        resolvingActionIcon={leadException.resolving_action_icon}
        approverRole={leadException.approver_role}
        workflowKey={workflowKey}
        level={level}
      />
    ) : undefined;

  // Warranty card alert - only surfaces status-workflow issues. Payment
  // blockers belong on the purchase/payment card and should not be
  // re-framed as DRAFT warranty issues.
  const warrantyLeadException = pickLeadException(statusExceptions);
  const warrantyAlertMessage =
    warrantyLeadException?.message ||
    "Awaiting activation before this warranty can be issued.";
  const warrantyAlertLevel = severityToLevel(
    warrantyLeadException?.severity ?? null,
  );
  const warrantyAlertAction = buildIssueAction(
    warrantyLeadException,
    warrantyAlertLevel,
    warrantyLeadException?.workflow_key ?? STATUS_WORKFLOW_KEY,
  );
  // The lifecycle "draft" state carries a tenant-configured label (OC calls it
  // "Inactive"; WPS-82) - use it for the card exception badge so the vocabulary
  // matches the status pill, falling back to "Draft" for tenants that keep it.
  const draftStatusLabel = (
    (statusWorkflowState?.state_key === "draft"
      ? statusWorkflowState?.state_label
      : undefined) || "Draft"
  ).toUpperCase();

  // Payment card alert - only surfaces issues that originated in the
  // payment workflow so the purchase card highlights the specific
  // blocker the operator needs to clear (e.g. "Payment overdue").
  // Primary signal is ``workflow_key`` (added in DRF in 11/2026); we
  // also fall back to issue_kind / resolving_action_key heuristics so
  // the alert lights up even on older API responses or when the
  // backend hasn't been restarted to pick up the serializer change.
  const PAYMENT_ISSUE_KINDS = new Set([
    "no_payment",
    "payment_overdue",
    "payment_required",
  ]);
  const PAYMENT_ACTION_KEYS = new Set(["mark_paid", "waive_payment"]);
  const paymentExceptions = pendingExceptions.filter((exc) => {
    if (exc.workflow_key === PAYMENT_WORKFLOW_KEY) return true;
    if (exc.workflow_key && exc.workflow_key !== PAYMENT_WORKFLOW_KEY)
      return false;
    if (exc.issue_kind && PAYMENT_ISSUE_KINDS.has(exc.issue_kind)) return true;
    if (
      exc.resolving_action_key &&
      PAYMENT_ACTION_KEYS.has(exc.resolving_action_key)
    )
      return true;
    return false;
  });
  const paymentLeadException = pickLeadException(paymentExceptions);
  const paymentAlertLevel = severityToLevel(
    paymentLeadException?.severity ?? null,
  );
  const paymentAlertAction = buildIssueAction(
    paymentLeadException,
    paymentAlertLevel,
    PAYMENT_WORKFLOW_KEY,
  );

  return (
    <AppShell>
      <main className="page-container pb-8 pb-12">
        <div className="flex flex-col border-[0.5px] border-[var(--rule)] border-t-0 px-6 pt-8 rounded-b-[8px] mb-8">
          {/* Back link */}
          <button
            onClick={back.go}
            className="mb-5 flex cursor-pointer items-center gap-1 border-none bg-transparent text-[11px] uppercase tracking-[0.1em] text-[var(--ink-3)] transition-colors duration-150"
          >
            <ArrowLeft className="h-3 w-3" />
            {back.label}
          </button>

          {/* Fallback header - only renders when there is no host record to
            anchor the page. When a host exists, identity, status, meta and
            primary actions all live in the host hero below. */}
          {!host && (
            <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between mb-7 gap-3">
              <div>
                <div className="flex items-center gap-3 flex-wrap">
                  <h1 className="text-[28px] sm:text-[42px] leading-none font-display text-[var(--ink)]">
                    {productTitle}
                  </h1>
                  <StatusBadge status={status} />
                </div>
                <p className="mt-1.5 font-mono text-[14px] tracking-[0.04em] text-[var(--ink-2)]">
                  REG # {formatRegistrationReference(holding)}
                </p>
                <p className="mt-1 text-[12px] text-[var(--ink-3)]">
                  Registered {formatDate(holding.created_at)} &middot;{" "}
                  {holding.organisation?.name || "-"}
                  {(claimsData?.results?.length ?? 0) > 0 && (
                    <button
                      onClick={() => {
                        if (claimsData!.results.length === 1) {
                          navigate(`/claims/${claimsData!.results[0].uuid}`);
                        } else {
                          document
                            .getElementById("claims-card")
                            ?.scrollIntoView({ behavior: "smooth" });
                        }
                      }}
                      className="ml-1.5 cursor-pointer border-none bg-transparent p-0 text-[12px] text-[var(--status-warn)] transition-opacity hover:opacity-70"
                    >
                      &middot; {claimsData!.results.length} active claim
                      {claimsData!.results.length !== 1 ? "s" : ""}
                    </button>
                  )}
                </p>
              </div>
              <div className="flex items-center gap-2 mb-0.5">
                <Button
                  onClick={() => navigate(`/claims/submit?holding=${uuid}`)}
                  disabled={
                    statusWorkflowState?.state_key === "draft" ||
                    holding.status === "draft"
                  }
                  title={
                    statusWorkflowState?.state_key === "draft" ||
                    holding.status === "draft"
                      ? "This registration is still pending approval - claims are blocked until it is activated."
                      : undefined
                  }
                  className="cursor-pointer rounded-[4px] border-none bg-[var(--green)] px-3.5 py-2 text-[11px] uppercase tracking-[0.09em] text-white disabled:cursor-not-allowed disabled:opacity-50"
                >
                  + Claim
                </Button>
                <AccessLevelPanel
                  resourceType="holding"
                  objectId={holding.id ?? null}
                />
                <ConfigHelp
                  sections={[
                    "display.reg_card",
                    "display.product_card",
                    "display.customer_card",
                    "display.purchase_card",
                    "display.warranty_card",
                  ]}
                  returnLabel="Registration"
                  params={{
                    warrantyTemplateUuid:
                      holding.warranties?.[0]?.warranty_template?.uuid ?? null,
                  }}
                  size="icon-lg"
                  className="border-[0.5px] border-[var(--rule-strong)] hover:bg-[var(--bg-subtle)]"
                />
              </div>
            </div>
          )}

          {/* Wide host header - property/vehicle/vessel identity + current
            owner & contact. Renders only when a host is linked. Styled as a
            Clara Vale hero: uppercase label, serif title, muted
            subtitle, primary CTA. */}
          {host &&
            (() => {
              const hostLabel =
                hostTemplate?.name ||
                (hostKind === "PROPERTY"
                  ? "Property"
                  : hostKind === "VESSEL"
                    ? "Vessel"
                    : "Vehicle");

              const propertyAddress = addressDisplay(host?.address);

              const hostTitle =
                hostKind === "PROPERTY"
                  ? propertyAddress || host.name || "-"
                  : host.name ||
                    host.registration_number ||
                    hostAddress.address_line_1 ||
                    "-";

              // Subtitle: for properties we surface the registered product so
              // operators see what's actually warranted at this address. For
              // vehicles/vessels we keep the existing identity strip.
              const productName = productTitle || null;
              let hostSubtitle = "";
              if (hostKind === "PROPERTY") {
                hostSubtitle = productName ?? "";
              } else if (hostKind === "VESSEL") {
                const parts: string[] = [];
                if (host.vin) parts.push(`HIN ${host.vin}`);
                hostSubtitle = parts.join(" · ");
              } else {
                const parts: string[] = [];
                if (host.vin) parts.push(`VIN ${host.vin}`);
                if (
                  host.registration_number &&
                  host.registration_number !== host.name
                ) {
                  parts.push(host.registration_number);
                }
                hostSubtitle = parts.join(" · ");
              }

              const sinceIso = holding.activated_at || holding.created_at;
              const sinceLabel = sinceIso
                ? new Date(sinceIso).toLocaleDateString(locale, {
                    day: "numeric",
                    month: "long",
                    year: "numeric",
                  })
                : null;

              const claimsBlocked =
                statusWorkflowState?.state_key === "draft" ||
                holding.status === "draft";
              const activeClaims = claimsData?.results ?? [];

              return (
                <section className="mb-10 border-b-[0.5px] border-[var(--rule)] pb-7">
                  <div className="mb-4 flex flex-col gap-6 sm:flex-row sm:items-end sm:justify-between">
                    <div className="min-w-0 flex-1">
                      <div className="mb-3.5 flex items-center gap-2.5">
                        <span className="text-[11px] font-normal uppercase tracking-[0.14em] text-[var(--ink-3)]">
                          {hostLabel}
                        </span>
                        <StatusBadge status={status} />
                      </div>
                      <h2 className="m-0 mb-2.5 text-[28px] !font-bold leading-[1.05] tracking-[-0.01em] text-[var(--ink)] font-display sm:text-[44px]">
                        {hostTitle}
                      </h2>
                      {hostSubtitle && (
                        <div className="text-[13px] font-light text-[var(--ink-2)]">
                          {hostSubtitle}
                        </div>
                      )}
                      <p className="mt-2 text-[12px] text-[var(--ink-3)]">
                        Registered {formatDate(holding.created_at)} &middot;{" "}
                        {holding.organisation?.name || "-"}
                        {activeClaims.length > 0 && (
                          <button
                            onClick={() => {
                              if (activeClaims.length === 1) {
                                navigate(`/claims/${activeClaims[0].uuid}`);
                              } else {
                                document
                                  .getElementById("claims-card")
                                  ?.scrollIntoView({ behavior: "smooth" });
                              }
                            }}
                            className="ml-1.5 cursor-pointer border-none bg-transparent p-0 text-[12px] text-[var(--status-warn)] transition-opacity hover:opacity-70"
                          >
                            &middot; {activeClaims.length} active claim
                            {activeClaims.length !== 1 ? "s" : ""}
                          </button>
                        )}
                      </p>
                    </div>
                    <div className="flex shrink-0 items-center gap-2">
                      <Button
                        onClick={() =>
                          navigate(`/claims/submit?holding=${uuid}`)
                        }
                        disabled={claimsBlocked}
                        title={
                          claimsBlocked
                            ? "This registration is still pending approval - claims are blocked until it is activated."
                            : undefined
                        }
                        className="cursor-pointer rounded-[4px] border-none bg-[var(--green)] px-3.5 py-2.5 text-[11px] font-normal uppercase tracking-[0.1em] text-white hover:bg-[var(--green-hover)] disabled:cursor-not-allowed disabled:opacity-50"
                      >
                        + Claim
                      </Button>
                      <AccessLevelPanel
                        resourceType="holding"
                        objectId={holding.id ?? null}
                      />
                      <CardActionMenu
                        data-testid="registration-host-menu"
                        triggerLabel={`More ${hostLabel.toLowerCase()} actions`}
                        triggerClassName="h-[34px] w-[34px] rounded-[4px]"
                        items={[
                          ...(hostKind === "PROPERTY" && propertyAddress
                            ? [
                                {
                                  key: "update-address",
                                  label: "Update property address",
                                  icon: <MapPin />,
                                  onSelect: () =>
                                    openConfirm({
                                      eyebrow: hostLabel,
                                      title: "Update property address",
                                      description:
                                        "Editing the address rewrites the registered location on this warranty. The change is logged in the audit trail.",
                                      confirmLabel: "Update address",
                                      requireReason: true,
                                      reasonLabel: "Reason",
                                      reasonPlaceholder:
                                        "e.g. corrected unit number provided by homeowner",
                                      onConfirm: () =>
                                        toast.success(
                                          "Address update queued (demo)",
                                        ),
                                    }),
                                },
                                {
                                  key: "copy-address",
                                  label: "Copy address",
                                  icon: <Copy />,
                                  onSelect: () =>
                                    copyToClipboard(
                                      propertyAddress,
                                      "Address copied",
                                    ),
                                },
                              ]
                            : []),
                          ...(hostKind === "PROPERTY"
                            ? (() => {
                                // Structure values are either "Single Family
                                // Home" or "Other". Hide whichever matches the
                                // current value so we never offer a no-op.
                                const current = (
                                  structureType ?? ""
                                ).toLowerCase();
                                const isSingleFamilyHome =
                                  current.includes("single");
                                const isOther = current.includes("other");
                                const items: CardActionMenuItem[] = [];
                                if (!isSingleFamilyHome) {
                                  items.push({
                                    key: "change-structure-to-single-family-home",
                                    label: "Re-classify as Single Family Home",
                                    icon: <Home />,
                                    onSelect: () =>
                                      openExceptionPreview({
                                        triggerTitle:
                                          "Property structure (Single Family Home eligibility)",
                                        triggerDescription:
                                          "Switching this property's structure type to Single Family Home overrides the standard structure-type eligibility check. Pricing tiers, warranty eligibility, and downstream rules may change.",
                                        defaultReasonCode:
                                          "wrong_classification_at_intake",
                                        confirmLabel:
                                          "Re-classify as Single Family Home",
                                        successLabel:
                                          "Re-classify as Single Family Home",
                                      }),
                                  });
                                }
                                if (!isOther) {
                                  items.push({
                                    key: "change-structure-to-other",
                                    label: "Re-classify as Other",
                                    icon: <Building2 />,
                                    onSelect: () =>
                                      openExceptionPreview({
                                        triggerTitle:
                                          "Property structure (non-Single-Family eligibility)",
                                        triggerDescription:
                                          "Switching this property's structure type to Other (e.g. multi-family, commercial, or non-standard residential) overrides the standard structure-type eligibility check. Pricing tiers, warranty eligibility, and downstream rules may change.",
                                        defaultReasonCode:
                                          "wrong_classification_at_intake",
                                        confirmLabel: "Re-classify as Other",
                                        successLabel: "Re-classify as Other",
                                      }),
                                  });
                                }
                                return items;
                              })()
                            : []),
                          {
                            key: "copy-link",
                            label: "Copy registration link",
                            icon: <Link2 />,
                            onSelect: () =>
                              copyToClipboard(
                                window.location.href,
                                "Link copied",
                              ),
                          },
                          ...(primaryWorkflowHistoryInstanceUuid
                            ? [
                                {
                                  key: "view-history",
                                  label: "View workflow history",
                                  icon: <History />,
                                  onSelect: () => {
                                    document
                                      .getElementById(
                                        `workflow-history-${primaryWorkflowHistoryInstanceUuid}`,
                                      )
                                      ?.scrollIntoView({ behavior: "smooth" });
                                  },
                                },
                              ]
                            : []),
                          {
                            key: "delete-registration",
                            label: "Delete registration",
                            icon: <Trash2 />,
                            destructive: true,
                            onSelect: () =>
                              openConfirm({
                                eyebrow: hostLabel,
                                title: "Delete this registration",
                                description:
                                  "This permanently removes the registration, its warranty terms, and all associated audit history. This cannot be undone.",
                                confirmLabel: "Delete registration",
                                destructive: true,
                                requireReason: true,
                                reasonLabel: "Reason",
                                reasonPlaceholder:
                                  "e.g. duplicate registration; merged with #1234",
                                onConfirm: () =>
                                  toast.success(
                                    "Registration deletion queued (demo)",
                                  ),
                              }),
                          },
                        ]}
                      />
                    </div>
                  </div>

                  <div className="flex flex-wrap gap-x-10 gap-y-5 sm:gap-x-16">
                    <div>
                      <span className="mb-1 block text-[11px] font-normal uppercase tracking-[0.14em] text-[var(--ink-3)]">
                        Current owner
                      </span>
                      {/* Through to the customer record, where their details
                          are edited and their change history lives. Without
                          it this block is a dead end - the owner is named
                          here but there is no route to the record. */}
                      <Button
                        variant="link"
                        onClick={() => navigate(`/customers/${consumer.uuid}`)}
                        data-testid="registration-owner-link"
                        className="mb-0.5 h-auto justify-start p-0 text-[13px] font-light text-[var(--ink)] hover:text-[var(--green)]"
                      >
                        {[consumer.first_name, consumer.last_name]
                          .filter(Boolean)
                          .join(" ") || "-"}
                      </Button>
                      {sinceLabel && (
                        <div className="text-[11px] font-light text-[var(--ink-3)]">
                          Since {sinceLabel}
                        </div>
                      )}
                    </div>
                    {hostKind === "PROPERTY" && structureType && (
                      <div>
                        <span className="mb-1 block text-[11px] font-normal uppercase tracking-[0.14em] text-[var(--ink-3)]">
                          Structure type
                        </span>
                        <div className="mb-0.5 text-[13px] font-light text-[var(--ink)]">
                          {structureType}
                        </div>
                      </div>
                    )}
                    <div>
                      <span className="mb-1 block text-[11px] font-normal uppercase tracking-[0.14em] text-[var(--ink-3)]">
                        Contact
                      </span>
                      <div className="mb-0.5 text-[13px] font-light text-[var(--ink)]">
                        {consumer.email || "-"}
                      </div>
                      {consumer.phone_number && (
                        <div className="text-[11px] font-light text-[var(--ink-3)]">
                          {formatPhoneDisplay(consumer.phone_number)}
                        </div>
                      )}
                    </div>
                  </div>
                </section>
              );
            })()}
        </div>

        {/* Card grid */}
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <DetailSection label="Customer">
            <EditableSummaryDetailCard
              sectionLabel="Customer"
              showSectionLabel={false}
              title={customerName}
              subtitle={customerSubtitle}
              icon={<UserCircle className="h-3.5 w-3.5" />}
              editButtonTestId="registration-customer-edit-btn"
              editAsMenuItem
              menu={({ editAction }) => (
                <CardActionMenu
                  data-testid="registration-customer-menu"
                  items={[
                    ...(editAction ? [editAction] : []),
                    {
                      key: "view-profile",
                      label: "View customer profile",
                      icon: <ExternalLink />,
                      onSelect: () => navigate(`/customers/${consumer.uuid}`),
                    },
                    ...(consumer.email
                      ? [
                          {
                            key: "contact-customer",
                            label: "Contact customer",
                            icon: <Mail />,
                            disabled: !contactClaim,
                            onSelect: () => setContactCustomerOpen(true),
                          },
                          {
                            key: "copy-email",
                            label: "Copy email",
                            icon: <Copy />,
                            onSelect: () =>
                              copyToClipboard(
                                consumer.email ?? "",
                                "Email copied",
                              ),
                          },
                        ]
                      : []),
                    {
                      key: "update-mailing-address",
                      label: "Update mailing address",
                      icon: <Mail />,
                      onSelect: () =>
                        openConfirm({
                          eyebrow: "Mailing address",
                          title: "Update mailing address",
                          description:
                            "Change where the homeowner receives correspondence. The change is recorded in the audit trail.",
                          requireReason: true,
                          confirmLabel: "Save address",
                          onConfirm: () =>
                            toast.success("Mailing address updated"),
                        }),
                    },
                  ]}
                />
              )}
              items={
                [
                  {
                    field: defineField({
                      code_name: "first_name",
                      display_name: "First name",
                      field_type: "TEXT",
                    }),
                    value: consumer.first_name,
                  },
                  {
                    field: defineField({
                      code_name: "last_name",
                      display_name: "Last name",
                      field_type: "TEXT",
                    }),
                    value: consumer.last_name,
                  },
                  {
                    field: defineField({
                      code_name: "email",
                      display_name: "Email",
                      field_type: "EMAIL",
                    }),
                    value: consumer.email,
                    link: true,
                    onLinkClick: () =>
                      (window.location.href = `mailto:${consumer.email}`),
                  },
                  {
                    field: defineField({
                      code_name: "phone_number",
                      display_name: "Phone",
                      field_type: "PHONE",
                    }),
                    value: consumer.phone_number,
                  },
                  ...(() => {
                    // Prefer the tenant's generic consumer ADDRESS EAV
                    // attribute; fall back to the legacy mailing_address. Both
                    // pages resolve this through the same helper so the claim
                    // page's customer card shows the identical address.
                    if (!consumerFields.address) return [];
                    return [
                      {
                        field: defineField({
                          code_name: "consumer_address",
                          display_name: consumerFields.addressLabel,
                          field_type: "TEXT",
                        }),
                        value: consumerFields.address,
                        readOnly: true,
                      },
                    ];
                  })(),
                  {
                    field: defineField({
                      code_name: "_profile",
                      display_name: "Profile",
                      field_type: "TEXT",
                    }),
                    value: "View customer",
                    readOnly: true,
                    link: true,
                    onLinkClick: () => navigate(`/customers/${consumer.uuid}`),
                  },
                ] satisfies CardFieldItem[]
              }
              editConfig={{
                onSave: handleCustomerSave,
                isPending: editHolding.isPending,
                canEdit,
                requireReason: true,
              }}
            />
          </DetailSection>

          <DetailSection label="Product">
            <EditableSummaryDetailCard
              sectionLabel="Product"
              showSectionLabel={false}
              title={productTitle}
              subtitle={productSubtitle}
              icon={<Package className="h-3.5 w-3.5" />}
              items={productItems}
              menu={
                <CardActionMenu
                  data-testid="registration-product-menu"
                  items={[
                    ...(hasProductIdentification &&
                    holding.unidentified &&
                    (canEdit || can("holdings.edit.acl"))
                      ? [
                          {
                            key: "identify-product",
                            label: "Identify product",
                            icon: <Package />,
                            onSelect: () => setIdentifyProductOpen(true),
                          },
                        ]
                      : []),
                    {
                      key: "open-product",
                      label: "Open product",
                      icon: <ExternalLink />,
                      onSelect: () =>
                        navigate(`/manage/products/${product.uuid}`),
                    },
                    {
                      key: "change-component-rules",
                      label: "Override component rules",
                      icon: <ShieldOff />,
                      onSelect: () =>
                        openExceptionPreview({
                          triggerTitle:
                            "Component logic requirement (Platinum eligibility)",
                          triggerDescription:
                            "This override pairs the product with a Platinum warranty even though the installed components don't meet the standard requirement.",
                          defaultReasonCode: "approved_substitute_components",
                          confirmLabel: "Override component rule",
                          successLabel: "Override component rule",
                        }),
                    },
                    {
                      key: "copy-product-name",
                      label: "Copy product name",
                      icon: <Copy />,
                      onSelect: () =>
                        copyToClipboard(productTitle, "Product copied"),
                    },
                  ]}
                />
              }
            />
            <IdentifyProductDialog
              holdingUuid={holding.uuid}
              open={identifyProductOpen}
              onOpenChange={setIdentifyProductOpen}
            />
          </DetailSection>

          <DetailSection label={warrantyNounLabel}>
            <SummaryDetailCard
              sectionLabel={warrantyNounLabel}
              showSectionLabel={false}
              title={
                warranties.length > 0
                  ? warranties[0].class_name || "Warranty coverage"
                  : activeSyntheticWarranty
                    ? pendingWarranty?.class_name || "Warranty coverage"
                    : pendingWarranty?.class_name ||
                      pendingWarranty?.option_display_name ||
                      "No live warranty"
              }
              subtitle={
                warranties.length > 0
                  ? `${warranties.length} warrant${warranties.length === 1 ? "y" : "ies"} linked to this registration.`
                  : activeSyntheticWarranty
                    ? "Registration is live. Certificate and warranty card details are ready."
                    : pendingWarranty
                      ? "Warranty is pending while this registration is waiting to go live."
                      : "This registration has not issued a warranty yet."
              }
              icon={<Shield className="h-3.5 w-3.5" />}
              className={
                warranties.length === 0 &&
                pendingWarranty &&
                !activeSyntheticWarranty
                  ? "opacity-[0.82]"
                  : undefined
              }
              alert={
                warrantyLeadException
                  ? {
                      level: warrantyAlertLevel,
                      leadingText: draftStatusLabel,
                      message: warrantyAlertMessage,
                      action: warrantyAlertAction,
                    }
                  : undefined
              }
              menu={
                <CardActionMenu
                  data-testid="registration-warranty-menu"
                  items={[
                    ...(warranties.length === 0
                      ? [
                          {
                            key: "activate-warranty",
                            label: "Activate warranty",
                            icon: <CheckCircle2 />,
                            onSelect: () =>
                              openExceptionPreview({
                                triggerTitle:
                                  "Standard activation requirements (payment, contractor, 90-day window)",
                                triggerDescription:
                                  "Activates the warranty even though one or more standard requirements (payment, contractor, or 90-day window) are not yet satisfied.",
                                defaultReasonCode: "late_evidence_provided",
                                confirmLabel: "Activate warranty",
                                successLabel: "Activate warranty",
                              }),
                          },
                        ]
                      : []),
                    ...(certificateDownloadEnabled
                      ? [
                          {
                            key: "print-certificate",
                            label:
                              warranties.length > 0
                                ? "Print certificate"
                                : "Print certificate (unavailable)",
                            icon: <Printer />,
                            disabled: warranties.length === 0,
                            onSelect: async () => {
                              try {
                                const res = await api.post(
                                  `/organisations/${orgUuid}/certificate-templates/preview/`,
                                  {
                                    template_slug: "registration_certificate",
                                    holding_uuid: uuid,
                                  },
                                );
                                const html =
                                  res.data?.data?.html ?? res.data?.html;
                                if (!html) {
                                  toast.error("Certificate template not found");
                                  return;
                                }
                                const win = window.open("", "_blank");
                                if (!win) return;
                                win.document.write(html);
                                win.document.close();
                                setTimeout(() => win.print(), 600);
                              } catch (err) {
                                toastApiError(
                                  err,
                                  "Failed to generate certificate",
                                );
                              }
                            },
                          },
                        ]
                      : []),
                    {
                      key: "override-90-day",
                      label: "Bypass 90-day window",
                      icon: <CalendarOff />,
                      onSelect: () =>
                        openExceptionPreview({
                          triggerTitle: "90-day registration window",
                          triggerDescription:
                            "Registers this warranty past the standard 90-day cut-off measured from the installation date.",
                          defaultReasonCode: "policy_grace_window",
                          confirmLabel: "Override 90-day window",
                          successLabel: "Override 90-day window",
                        }),
                    },
                    {
                      key: "upgrade-tier",
                      label: "Upgrade tier (System → Platinum)",
                      icon: <ArrowUpRight />,
                      onSelect: () =>
                        openExceptionPreview({
                          triggerTitle:
                            "Tier eligibility (component & contractor checks)",
                          triggerDescription:
                            "Promotes this warranty (e.g. System → Preferred or Platinum) and bypasses the standard tier eligibility checks.",
                          defaultReasonCode: "promotional_upgrade",
                          confirmLabel: "Upgrade tier",
                          successLabel: "Upgrade tier",
                        }),
                    },
                    {
                      key: "downgrade-tier",
                      label: "Downgrade to Standard",
                      icon: <ArrowDownRight />,
                      onSelect: () =>
                        openConfirm({
                          eyebrow: "Tier change",
                          title: "Downgrade to Standard warranty",
                          description:
                            "Move this extended warranty back to a Standard warranty. Coverage limits and term length will be reduced.",
                          requireReason: true,
                          confirmLabel: "Downgrade",
                          onConfirm: () => toast.success("Warranty downgraded"),
                        }),
                    },
                    {
                      key: "apply-prepaid-commercial",
                      label: "Apply prepaid (commercial / >75 squares)",
                      icon: <Tag />,
                      onSelect: () =>
                        openExceptionPreview({
                          triggerTitle:
                            "Prepaid eligibility (residential roofs ≤75 squares)",
                          triggerDescription:
                            "Applies a prepaid warranty to a commercial registration or a roof greater than 75 squares.",
                          defaultReasonCode: "commercial_pilot_program",
                          confirmLabel: "Apply prepaid",
                          successLabel: "Apply prepaid",
                        }),
                    },
                    {
                      key: "cancel-terms",
                      label: "Cancel / expire terms",
                      icon: <Ban />,
                      destructive: true,
                      onSelect: () =>
                        openConfirm({
                          eyebrow: "Destructive action",
                          title: "Cancel warranty terms",
                          description:
                            "Mark this warranty as no longer valid while keeping the registration on file. The customer will lose coverage immediately.",
                          requireReason: true,
                          destructive: true,
                          confirmLabel: "Cancel terms",
                          onConfirm: () =>
                            toast.success("Warranty terms cancelled"),
                        }),
                    },
                    ...(statusWorkflowInstanceUuid
                      ? [
                          {
                            key: "view-history",
                            label: "View workflow history",
                            icon: <History />,
                            onSelect: () => {
                              const target = document.getElementById(
                                `workflow-history-${statusWorkflowInstanceUuid}`,
                              );
                              if (target) {
                                target.scrollIntoView({
                                  behavior: "smooth",
                                  block: "start",
                                });
                              }
                            },
                          },
                        ]
                      : []),
                  ]}
                />
              }
              items={configuredWarrantySummaryItems}
              body={
                configuredWarrantySummaryItems.length === 0 ? (
                  <WarrantySummaryBody warranties={effectiveWarranties} />
                ) : undefined
              }
            />
          </DetailSection>

          {allAdditionalDetailItems.length > 0 && (
            <DetailSection label="Additional Details">
              <EditableSummaryDetailCard
                sectionLabel="Additional Details"
                showSectionLabel={false}
                title={`${allAdditionalDetailItems.length} captured field${allAdditionalDetailItems.length === 1 ? "" : "s"}`}
                subtitle="Registration-specific attributes and workflow state mirrors."
                icon={<Settings2 className="h-3.5 w-3.5" />}
                items={allAdditionalDetailItems}
                editAsMenuItem
                editConfig={{
                  onSave: handleAdditionalDetailsSave,
                  isPending: editHolding.isPending,
                  canEdit,
                  requireReason: true,
                }}
                collapsible
                previewCount={4}
                menu={({ editAction }) => (
                  <CardActionMenu
                    data-testid="registration-additional-menu"
                    items={[
                      ...(editAction ? [editAction] : []),
                      {
                        key: "export-fields",
                        label: "Export as JSON",
                        icon: <FileDown />,
                        onSelect: () => {
                          const payload = additionalAttrs.reduce<
                            Record<string, unknown>
                          >((acc, attr) => {
                            acc[attr.code_name] = attr.value;
                            return acc;
                          }, {});
                          const blob = new Blob(
                            [JSON.stringify(payload, null, 2)],
                            { type: "application/json" },
                          );
                          const url = URL.createObjectURL(blob);
                          const a = document.createElement("a");
                          a.href = url;
                          a.download = `registration-${uuid ?? "export"}-fields.json`;
                          a.click();
                          URL.revokeObjectURL(url);
                          toast.success("Fields exported");
                        },
                      },
                      {
                        key: "copy-fields",
                        label: "Copy as JSON",
                        icon: <Copy />,
                        onSelect: () => {
                          const payload = additionalAttrs.reduce<
                            Record<string, unknown>
                          >((acc, attr) => {
                            acc[attr.code_name] = attr.value;
                            return acc;
                          }, {});
                          copyToClipboard(
                            JSON.stringify(payload, null, 2),
                            "Fields copied",
                          );
                        },
                      },
                    ]}
                  />
                )}
              />
            </DetailSection>
          )}

          <DetailSection label="Purchase">
            <EditableSummaryDetailCard
              sectionLabel="Purchase"
              showSectionLabel={false}
              title={
                holding.purchase_price
                  ? formatCurrency(Number(holding.purchase_price))
                  : "Purchase details"
              }
              subtitle={
                holding.partnership?.downstream_organisation_name ||
                holding.organisation?.name ||
                "Commercial details captured at registration time."
              }
              icon={<Receipt className="h-3.5 w-3.5" />}
              items={purchaseItems}
              alert={
                paymentLeadException
                  ? {
                      level: paymentAlertLevel,
                      leadingText: "PAYMENT",
                      message: paymentLeadException.message,
                      action: paymentAlertAction,
                    }
                  : undefined
              }
              collapsible
              previewCount={4}
              menu={
                <CardActionMenu
                  data-testid="registration-purchase-menu"
                  items={[
                    {
                      key: "resync-cart",
                      label: "Resync to cart",
                      icon: <RefreshCw />,
                      onSelect: () =>
                        openConfirm({
                          eyebrow: "OCConnect",
                          title: "Resync to OCConnect cart",
                          description:
                            "Push this registration back to the OCConnect cart so the contractor can complete payment. Use this if the warranty failed to flow on first submission.",
                          confirmLabel: "Resync to cart",
                          onConfirm: () =>
                            toast.success("Pushed to OCConnect cart"),
                        }),
                    },
                    {
                      key: "mark-paid",
                      label: "Mark as paid (bypass payment)",
                      icon: <CheckCircle2 />,
                      onSelect: () =>
                        openExceptionPreview({
                          triggerTitle: "Standard payment requirement",
                          triggerDescription:
                            "Moves this registration to a paid state without an OCConnect transaction. Use only when payment is captured outside the standard flow.",
                          defaultReasonCode: "payment_outside_oc_connect",
                          confirmLabel: "Mark as paid",
                          successLabel: "Mark as paid",
                        }),
                    },
                    {
                      key: "return-to-cart",
                      label: "Return cancelled to cart",
                      icon: <RotateCcw />,
                      onSelect: () =>
                        openExceptionPreview({
                          triggerTitle:
                            "Cancellation finality (cancelled → cart)",
                          triggerDescription:
                            "Re-opens this cancelled, unpaid registration so the contractor can complete payment.",
                          defaultReasonCode: "cancellation_in_error",
                          confirmLabel: "Return to cart",
                          successLabel: "Return to cart",
                        }),
                    },
                    {
                      key: "edit-contractor",
                      label: "Edit assigned contractor",
                      icon: <UserCog />,
                      onSelect: () =>
                        openConfirm({
                          eyebrow: "Contractor",
                          title: "Reassign contractor",
                          description:
                            "Change the member ID / contractor on this unpaid extended warranty so payment is collected in the correct OCConnect account.",
                          requireReason: true,
                          confirmLabel: "Reassign",
                          onConfirm: () =>
                            toast.success("Contractor reassigned"),
                        }),
                    },
                    {
                      key: "void-unpaid",
                      label: "Void unpaid warranty",
                      icon: <Ban />,
                      destructive: true,
                      onSelect: () =>
                        openConfirm({
                          eyebrow: "Destructive action",
                          title: "Void unpaid warranty",
                          description:
                            "Park this unpaid warranty so it isn't treated as a duplicate when re-submitted. The original record is retained for audit.",
                          requireReason: true,
                          destructive: true,
                          confirmLabel: "Void warranty",
                          onConfirm: () => toast.success("Warranty voided"),
                        }),
                    },
                    ...(holding.purchase_price
                      ? [
                          {
                            key: "copy-amount",
                            label: "Copy purchase amount",
                            icon: <Copy />,
                            onSelect: () =>
                              copyToClipboard(
                                String(holding.purchase_price),
                                "Amount copied",
                              ),
                          },
                        ]
                      : []),
                    ...(paymentWorkflowInstanceUuid
                      ? [
                          {
                            key: "view-payment-history",
                            label: "View payment history",
                            icon: <History />,
                            onSelect: () => {
                              const target = document.getElementById(
                                `workflow-history-${paymentWorkflowInstanceUuid}`,
                              );
                              if (target) {
                                target.scrollIntoView({
                                  behavior: "smooth",
                                  block: "start",
                                });
                              }
                            },
                          },
                        ]
                      : []),
                  ]}
                />
              }
            />
          </DetailSection>
        </div>

        {/* Notes + Claims - side by side */}
        <div className="mt-6 grid grid-cols-1 gap-4 md:grid-cols-2">
          <NotesPanel
            recordId={uuid ?? ""}
            recordType="registration"
            canAddInternal={true}
            canAddCustomerVisible={true}
            stageLabel={status}
          />
          <div id="claims-card">
            <DetailCard
              title="Claims"
              icon={<FileText className="h-3.5 w-3.5" />}
              trailing={
                <button
                  onClick={() => navigate(`/claims/submit?holding=${uuid}`)}
                  className="inline-flex cursor-pointer items-center gap-1 border-none bg-transparent p-0 text-[10px] font-normal uppercase tracking-[0.1em] text-[var(--green)] transition-opacity duration-150 hover:opacity-70"
                >
                  <Plus className="h-3 w-3" />
                  Claim
                </button>
              }
            >
              {claimsPending ||
              (claimsFetching &&
                (!claimsData || claimsData.results.length === 0)) ? (
                <p className="py-4 text-center text-[12px] text-[var(--ink-3)]">
                  {t("common.loading")}
                </p>
              ) : !claimsData || claimsData.results.length === 0 ? (
                <p className="py-4 text-center text-[12px] text-[var(--ink-3)]">
                  No claims for this registration
                </p>
              ) : (
                <div className="flex flex-col gap-2">
                  {claimsData.results.map((claim) => (
                    <button
                      key={claim.uuid}
                      onClick={() => navigate(`/claims/${claim.uuid}`)}
                      className="flex w-full cursor-pointer items-center justify-between rounded-[6px] border-[0.5px] border-[var(--rule)] bg-[var(--bg)] px-3.5 py-3 text-left transition-colors hover:border-[var(--rule-strong)] hover:bg-[var(--bg-subtle)]"
                    >
                      <div className="flex min-w-0 flex-col gap-1">
                        <span className="truncate text-[13px] font-normal text-[var(--ink)]">
                          {claim.claim_schema_name}
                        </span>
                        <span className="text-[11px] text-[var(--ink-3)]">
                          <span className="font-mono tracking-[0.02em]">
                            {formatClaimId(claim.uuid)}
                          </span>
                          {" · "}
                          {new Date(claim.created_at).toLocaleDateString(
                            locale,
                            {
                              day: "numeric",
                              month: "short",
                              year: "numeric",
                            },
                          )}
                          {claim.assigned_to && (
                            <> · {claim.assigned_to.name}</>
                          )}
                        </span>
                      </div>
                      <StatusBadge
                        status={claim.claim_state || "Open"}
                        color={claim.claim_state_color}
                        className="ml-3 shrink-0"
                      />
                    </button>
                  ))}
                </div>
              )}
            </DetailCard>
          </div>
        </div>

        {/* Workflow history - append-only audit of every transition. */}
        {workflowHistoryStates.map((workflowState) => (
          <div
            key={workflowState.instance_uuid}
            id={`workflow-history-${workflowState.instance_uuid}`}
            className="mt-6 scroll-mt-24"
          >
            <WorkflowHistoryCard
              instanceUuid={workflowState.instance_uuid}
              title={`${workflowState.workflow_label || workflowState.workflow_key} history`}
            />
            <EntityHistoryCard
              workflowInstanceUuid={workflowState.instance_uuid}
              title={`${workflowState.workflow_label || workflowState.workflow_key} entity log`}
              className="mt-4"
            />
          </div>
        ))}

        {/* Holding-level audit trail - captures all field edits regardless
            of which workflow (if any) they belong to. The warranty keys are
            included alongside it because the tier / terms actions run against
            the "warranty" entity, so their audit entries are written under
            that entity key rather than the holding's - without them an
            upgrade / downgrade leaves no visible trace here (WPS-180). */}
        <EntityHistoryCard
          entityKeys={["holding", "warranty"]}
          entityIds={[uuid, ...warranties.map((w) => w.uuid)]}
          title="Holding entity log"
          className="mt-6"
        />
      </main>

      {confirmConfig && (
        <DemoConfirmModal
          {...confirmConfig}
          open={confirmConfig !== null}
          onOpenChange={(next) => {
            if (!next) setConfirmConfig(null);
          }}
        />
      )}
      {previewConfig && (
        <WorkflowIssueResolveModal
          uuid={null}
          preview={previewConfig}
          open={previewConfig !== null}
          onOpenChange={(next) => {
            if (!next) setPreviewConfig(null);
          }}
        />
      )}
      {contactClaim ? (
        <ContactCustomerDialog
          open={contactCustomerOpen}
          onOpenChange={setContactCustomerOpen}
          customerName={customerName || "the customer"}
          claimUuid={contactClaim.uuid}
        />
      ) : null}
    </AppShell>
  );
}

type PropertyRecordWarrantyRow = {
  uuid: string;
  holdingUuid: string;
  structureUuid: string | null;
  structure: string;
  product: string;
  variant: string;
  serialNumber: string;
  serialLabel: string;
  warrantyClass: string;
  reference: string;
  paymentState: WorkflowStateRef | null;
  paymentWorkflowInstanceUuid?: string | null;
  status: string;
  statusKey: string;
  statusNeedsAttention: boolean;
  canOpenRecord: boolean;
  type: string;
  term: string;
  installed: string;
  needsAction: boolean;
  issueUuid?: string | null;
  statusWorkflowInstanceUuid?: string | null;
};

type PropertyStructureGroup = {
  uuid: string;
  name: string;
  meta: string;
  sqft: string;
  built: string;
  rows: PropertyRecordWarrantyRow[];
};

function getRegistrationInstallationDate(registration: Holding): string | null {
  const value = registration.attribute_values?.find(
    (attr) => attr.code_name === "installation_date",
  )?.value;
  if (value === null || value === undefined || value === "") return null;
  return String(value);
}

function getOtherStructureTypeValue(registration: Holding): string {
  const value = humaniseDetailValue(
    registration.registration_field_values?.other_structure_type,
  );
  return value ? titleCaseStructureLabel(value) : "";
}

function structureEntityAttributeToField(
  attr: EntityAttributeDefinition,
): FormFieldMeta {
  return defineField({
    code_name: attr.code_name,
    display_name: attr.display_name,
    field_type: attr.field_type as FieldType,
    required: attr.required,
    hint: attr.hint || undefined,
    description: attr.description || undefined,
    options: attr.configuration_data.options,
    order: attr.order,
    configuration_data: attr.configuration_data,
    source_uuid: attr.uuid,
  });
}

function PropertyRegistrationRecordV4({
  holding,
  status,
  structureType,
  onBack,
  backLabel,
}: {
  holding: Holding;
  status: string;
  structureType?: string;
  onBack: () => void;
  backLabel: string;
}) {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState("Structures");
  const [filter, setFilter] = useState<"ALL" | "ACTIVE" | "NEEDS ACTION">(
    "ALL",
  );
  const [search, setSearch] = useState("");
  const [view, setView] = useState<"rows" | "grid">("rows");
  const [structureOpenByUuid, setStructureOpenByUuid] = useState<
    Record<string, boolean>
  >({});
  const [addStructureOpen, setAddStructureOpen] = useState(false);
  const navigate = useNavigate();
  const fromClaim = useFromClaim();
  const queryClient = useQueryClient();
  const { formatDate } = useFormatting();
  const { can } = usePermissions();
  const { isPartnerAccess } = useOrg();
  const { data: entityTypes = [] } = useEntityTypes();
  const holdingEntity = useMemo(
    () => entityTypes.find((entity) => entity.key === "holding"),
    [entityTypes],
  );
  const identityCodes = useMemo(
    () => identityAttributeCodes(holdingEntity),
    [holdingEntity],
  );
  const structureEntity = useMemo(
    () =>
      entityTypes.find(
        (entity) =>
          String(entity.metadata?.storage ?? "") === "hosts.Structure",
      ),
    [entityTypes],
  );
  // The tenant's own word for its host, same source the vehicle record reads.
  const hostNoun = useMemo(
    () =>
      entityTypes.find((entity) => entity.key === "host")?.label?.trim() ||
      "property",
    [entityTypes],
  );
  const canViewStructures =
    !structureEntity || canEntityPermission(can, structureEntity.key, "view");
  const canCreateStructure =
    !!structureEntity &&
    canEntityPermission(can, structureEntity.key, "create");
  // Mirrors the codenames the holding attachment subject adapter accepts for
  // upload/delete, so the card never offers an action the API will reject.
  const canManageDocuments =
    can("holdings.edit.global") ||
    can("holdings.create") ||
    can("attachments.upload");

  const host = holding.host;
  // See the note on `hostAddress` above - flat host address columns are not
  // serialised; they come off the canonical envelope.
  const hostAddress = toHostAddressColumns(host?.address);
  const propertyHostUuid = host?.uuid ?? null;
  const consumer = holding.consumer_account;
  const propertyHoldingsParams = useMemo(
    () => ({
      host_uuid: host?.uuid ?? "00000000-0000-0000-0000-000000000000",
      page_size: "500",
    }),
    [host?.uuid],
  );
  const { data: propertyHoldingsData, isLoading: propertyHoldingsLoading } =
    useHoldings(propertyHoldingsParams);
  const {
    data: propertyStructures = [],
    isLoading: propertyStructuresLoading,
  } = useQuery<StructureSummary[]>({
    queryKey: ["host-structures", propertyHostUuid],
    queryFn: () =>
      api
        .get<StructureSummary[]>(`/hosts/${propertyHostUuid}/structures/`)
        .then((response) => response.data),
    enabled: !!propertyHostUuid && canViewStructures,
  });
  const structure = holding.structure;

  const addressTitle = hostAddress.address_line_1 || host?.name || "Property";
  const cityLine = [
    hostAddress.address_line_2,
    hostAddress.address_town_or_city,
    hostAddress.address_county,
    hostAddress.address_postcode,
    hostAddress.address_country,
  ]
    .map((part) => (part ?? "").trim())
    .filter(Boolean)
    .join(", ");
  const ownerName =
    [consumer.first_name, consumer.last_name].filter(Boolean).join(" ") ||
    "Customer";
  const structureValues = useMemo(
    () => structure?.attribute_values ?? {},
    [structure?.attribute_values],
  );
  const structureName =
    structure?.label ||
    humaniseDetailValue(structureValues.name) ||
    humaniseDetailValue(structureValues.structure_name) ||
    "Main structure";
  const propertyHostColumnValues = useMemo(
    () => ({
      address_line_1: hostAddress.address_line_1,
      address_line_2: hostAddress.address_line_2,
      address_town_or_city: hostAddress.address_town_or_city,
      address_county: hostAddress.address_county,
      address_postcode: hostAddress.address_postcode,
      address_country: hostAddress.address_country,
    }),
    [
      hostAddress.address_county,
      hostAddress.address_country,
      hostAddress.address_line_1,
      hostAddress.address_line_2,
      hostAddress.address_postcode,
      hostAddress.address_town_or_city,
    ],
  );
  const propertyHostAttributeValues = useMemo(() => {
    const values: Record<string, unknown> = {};
    for (const attr of host?.attribute_values ?? []) {
      values[attr.code_name] = attr.value;
    }
    for (const [key, value] of Object.entries(propertyHostColumnValues)) {
      if (value !== undefined && value !== null && value !== "") {
        values[key] = value;
      }
    }
    for (const [key, value] of Object.entries(structureValues)) {
      if (value !== undefined && value !== null && value !== "") {
        values[key] = value;
      }
    }
    if (structureType && values.structure_type === undefined) {
      values.structure_type = structureType;
    }
    return values;
  }, [
    host?.attribute_values,
    propertyHostColumnValues,
    structureType,
    structureValues,
  ]);
  const startRegistrationForProperty = useCallback(
    (
      targetStructureValues: Record<string, unknown> = structureValues,
      structureUuid?: string | null,
    ) => {
      // Build the composite address object for ADDRESS-type host template fields
      // (e.g. OC's `address` attribute with field_type=ADDRESS, lookup_provider=address).
      // The wizard's AddressPanel expects { addressLine1, addressLine2, locality,
      // region, postalCode, countryCode }, mapped from the host's column values.
      const compositeAddress =
        hostAddress.address_line_1 ||
        hostAddress.address_town_or_city ||
        hostAddress.address_postcode
          ? {
              addressLine1: hostAddress.address_line_1,
              addressLine2: hostAddress.address_line_2,
              locality: hostAddress.address_town_or_city,
              region: hostAddress.address_county,
              postalCode: hostAddress.address_postcode,
              countryCode: hostAddress.address_country,
            }
          : undefined;

      // Extract early attribute values (pitch, stories, etc.) from an
      // existing holding on the target structure so the wizard pre-fills
      // them. Contractor (installer_name) is intentionally excluded as it
      // may differ per install.
      const earlyAttributesPrefill: Record<string, unknown> = {};
      if (structureUuid) {
        const allHoldings = propertyHoldingsData?.results ?? [];
        const referenceHolding =
          allHoldings.find((h) => h.structure?.uuid === structureUuid) ??
          (holding.structure?.uuid === structureUuid ? holding : null);
        for (const attr of referenceHolding?.attribute_values ?? []) {
          if (
            attr.code_name !== "installer_name" &&
            attr.value != null &&
            attr.value !== ""
          ) {
            earlyAttributesPrefill[attr.code_name] = attr.value;
          }
        }
      }

      navigate("/registrations/new", {
        state: {
          propertyRegistrationPrefill: {
            hostUuid: host?.uuid ?? null,
            // Merge host-level values first, then let the clicked structure's
            // own attribute values take precedence (avoids stale structure_type
            // from the current holding bleeding into a different structure).
            // Also inject the composite address object so the ADDRESS-type
            // attribute (e.g. `address`) is pre-populated in the wizard.
            hostAttributeValues: {
              ...propertyHostAttributeValues,
              ...(compositeAddress ? { address: compositeAddress } : {}),
              ...targetStructureValues,
            },
            hostColumnValues: propertyHostColumnValues,
            structureAttributeValues: targetStructureValues,
            // Pre-fill the customer step with the current property owner.
            // Contractor is intentionally excluded, as it may differ per install.
            customerPrefill: {
              email: consumer?.email ?? "",
              firstName: consumer?.first_name ?? "",
              lastName: consumer?.last_name ?? "",
              phone: consumer?.phone_number ?? "",
            },
            earlyAttributesPrefill,
          },
        },
      });
    },
    [
      consumer?.email,
      consumer?.first_name,
      consumer?.last_name,
      consumer?.phone_number,
      holding,
      host?.uuid,
      hostAddress.address_line_1,
      hostAddress.address_line_2,
      hostAddress.address_town_or_city,
      hostAddress.address_county,
      hostAddress.address_postcode,
      hostAddress.address_country,
      navigate,
      propertyHostAttributeValues,
      propertyHostColumnValues,
      propertyHoldingsData?.results,
      structureValues,
    ],
  );
  const structureFields = useMemo(
    () =>
      (structureEntity?.attributes ?? [])
        .filter(
          (attr) =>
            attr.source !== "COMPUTED" &&
            attr.editable &&
            !attr.code_name.startsWith("address_"),
        )
        .sort((a, b) => a.order - b.order)
        .map(structureEntityAttributeToField),
    [structureEntity?.attributes],
  );
  const addStructureInitialValues = useMemo(() => {
    const values: Record<string, unknown> = {};
    for (const field of structureFields) {
      const addressValue =
        propertyHostColumnValues[field.code_name] ??
        propertyHostAttributeValues[field.code_name];
      if (
        addressValue !== undefined &&
        addressValue !== null &&
        addressValue !== ""
      ) {
        values[field.code_name] = addressValue;
      }
    }
    return values;
  }, [propertyHostAttributeValues, propertyHostColumnValues, structureFields]);
  const [isCreatingStructure, setIsCreatingStructure] = useState(false);
  const createStructure = useCallback(
    async (values: Record<string, unknown>) => {
      if (!propertyHostUuid) return;
      setIsCreatingStructure(true);
      try {
        await api.post(`/hosts/${propertyHostUuid}/structures/`, {
          attribute_values: values,
        });
        await Promise.all([
          queryClient.invalidateQueries({
            queryKey: ["holding", holding.uuid],
          }),
          queryClient.invalidateQueries({ queryKey: ["holdings"] }),
          queryClient.invalidateQueries({
            queryKey: ["host-structures", propertyHostUuid],
          }),
        ]);
        toast.success("Structure added");
      } catch (err) {
        toastApiError(err, "Failed to add structure");
        throw err;
      } finally {
        setIsCreatingStructure(false);
      }
    },
    [holding.uuid, propertyHostUuid, queryClient],
  );
  const openWarrantyRecord = useCallback(
    (row: PropertyRecordWarrantyRow) => {
      if (!row.canOpenRecord) return;
      navigate(
        withFromClaim(
          `/registrations/${row.holdingUuid}/warranties/${row.uuid}`,
          fromClaim,
        ),
      );
    },
    [navigate, fromClaim],
  );
  const handleWarrantyRowKeyDown = useCallback(
    (event: KeyboardEvent<HTMLDivElement>, row: PropertyRecordWarrantyRow) => {
      if (!row.canOpenRecord) return;
      if (event.key !== "Enter" && event.key !== " ") return;
      event.preventDefault();
      openWarrantyRecord(row);
    },
    [openWarrantyRecord],
  );
  // Download a record's certificate as a PDF. Renders off the same
  // certificate template the on-screen certificate uses, so the file the
  // customer receives and what staff see can never disagree (WPS-179).
  const [downloadingCertificate, setDownloadingCertificate] = useState<
    string | null
  >(null);
  const downloadCertificate = useCallback(
    async (holdingUuid: string, reference: string) => {
      // Without an organisation the url interpolates to
      // ``/organisations/undefined/...``, so the request is guaranteed to
      // fail. Fail here instead of round-tripping - but still say so, because
      // a clicked menu item that does nothing reads as the app being broken.
      const organisationUuid = holding.organisation?.uuid;
      if (!organisationUuid) {
        toast.error(
          t(
            "registrations.certificateDownloadFailed",
            "Couldn't download the certificate.",
          ),
        );
        return;
      }
      setDownloadingCertificate(holdingUuid);
      try {
        const res = await api.post(
          `/organisations/${organisationUuid}/certificate-templates/preview/`,
          {
            template_slug: "registration_certificate",
            holding_uuid: holdingUuid,
            format: "pdf",
          },
          { responseType: "blob" },
        );
        const url = URL.createObjectURL(res.data as Blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = `${reference || "certificate"}.pdf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      } catch (err) {
        toastApiError(
          err,
          t(
            "registrations.certificateDownloadFailed",
            "Couldn't download the certificate.",
          ),
        );
      } finally {
        setDownloadingCertificate(null);
      }
    },
    [holding.organisation?.uuid, t],
  );

  // Appended to every record row's action menu. Defined once because the
  // page renders that same row menu from two places - the structure-grouped
  // table and the flat records table - and an item added to only one of them
  // is invisible in whichever view the user happens to be in.
  const recordMenuExtraItems = useCallback(
    (row: PropertyRecordWarrantyRow): CardActionMenuItem[] => [
      {
        key: "download-certificate",
        label:
          downloadingCertificate === row.holdingUuid
            ? t(
                "registrations.preparingCertificate",
                "Preparing certificate...",
              )
            : t(
                "registrations.downloadCertificate",
                "Download certificate (PDF)",
              ),
        icon: <FileDown />,
        disabled: downloadingCertificate === row.holdingUuid,
        onSelect: () => downloadCertificate(row.holdingUuid, row.reference),
      },
    ],
    // ``t`` is stable across renders, so listing it changes nothing at
    // runtime - it is named only to satisfy exhaustive-deps, which cannot
    // know that. The file carries no eslint-disable directives; keep it so.
    [downloadCertificate, downloadingCertificate, t],
  );

  const structureMenuItems = (
    group: PropertyStructureGroup,
  ): CardActionMenuItem[] => [
    {
      key: "add-product",
      label: `Add product to ${group.name.toLowerCase()}`,
      icon: <Plus />,
      onSelect: () => {
        const targetStructure = propertyStructures.find(
          (item) => item.uuid === group.uuid,
        );
        startRegistrationForProperty(
          targetStructure?.attribute_values ?? {},
          group.uuid,
        );
      },
    },
  ];

  const propertyHoldings = useMemo(() => {
    const holdings = propertyHoldingsData?.results ?? [];
    if (holdings.length === 0) return [holding];
    return holdings.map((item) =>
      item.uuid === holding.uuid
        ? {
            ...item,
            pending_exceptions: holding.pending_exceptions,
            pending_warranty_summary: holding.pending_warranty_summary,
          }
        : item,
    );
  }, [holding, propertyHoldingsData?.results]);

  const buildWarrantyRows = useMemo(
    () =>
      (registration: Holding): PropertyRecordWarrantyRow[] => {
        const registrationWarranties = registration.warranties ?? [];
        const registrationPendingWarranty = plannedWarranty(registration);
        const registrationWorkflowState = pickWorkflowState(
          registration.workflow_states ?? [],
          STATUS_WORKFLOW_KEY,
        );
        const registrationPaymentWorkflowState = pickWorkflowState(
          registration.workflow_states ?? [],
          PAYMENT_WORKFLOW_KEY,
        );
        const registrationPendingExceptions =
          registration.pending_exceptions ?? [];
        const primaryIssueUuid = registrationPendingExceptions[0]?.uuid ?? null;
        const derivedRegistrationStatus = deriveStatus(
          registration.status,
          registrationPendingExceptions.length,
          registrationWarranties,
        );
        const registrationStatusLabel =
          registration.status === "cancelled" ||
          registration.status === "transferred"
            ? derivedRegistrationStatus
            : registrationWorkflowState?.state_label ||
              derivedRegistrationStatus;
        const registrationStatusKey = (
          registration.status === "cancelled" ||
          registration.status === "transferred"
            ? registration.status
            : registrationWorkflowState?.state_key ||
              registration.status ||
              registrationStatusLabel
        ).toLowerCase();
        const registrationStatusNeedsAttention =
          registrationWorkflowState?.state_key === "draft" &&
          registrationPendingExceptions.length > 0;
        const registrationNeedsAction =
          registrationPendingExceptions.length > 0 ||
          ["draft", "pending", "pending_approval", "needs_action"].includes(
            registrationStatusKey,
          );
        const registrationStructureValues =
          registration.structure?.attribute_values ?? {};
        const registrationStructureType =
          humaniseDetailValue(registrationStructureValues.structure_type) ||
          structureType;
        const otherStructureType = getOtherStructureTypeValue(registration);
        const registrationStructureName =
          (isOtherStructureValue(registrationStructureType)
            ? otherStructureType
            : "") ||
          registration.structure?.label ||
          humaniseDetailValue(registrationStructureValues.name) ||
          humaniseDetailValue(registrationStructureValues.structure_name) ||
          "Main structure";

        const warrantyRows =
          registrationWarranties.length > 0
            ? registrationWarranties
            : registrationPendingWarranty
              ? [registrationPendingWarranty]
              : [null];

        return warrantyRows.map((warranty) => {
          const isProductOnly = warranty === null;
          const isPending = warranty !== null && "uuid" in warranty === false;
          const warrantyType =
            warranty === null
              ? "-"
              : (WARRANTY_TYPE_LABELS[warranty.warranty_type] ??
                humaniseDetailValue(warranty.warranty_type));
          const className =
            warranty === null
              ? "No warranty issued"
              : "class_name" in warranty
                ? warranty.class_name
                : warranty.class_name || warranty.option_display_name;
          // A recorded install date or an issued warranty's start, never the record's
          // creation date: a migrated record can genuinely have no install date.
          const installedDate =
            getRegistrationInstallationDate(registration) ??
            (isPending ? null : warranty?.active_from);
          const identity = holdingIdentityField(registration, identityCodes);
          return {
            uuid: isProductOnly
              ? `product-${registration.uuid}`
              : isPending
                ? "pending-warranty"
                : warranty.uuid,
            holdingUuid: registration.uuid,
            structureUuid: registration.structure?.uuid ?? null,
            structure: registrationStructureName,
            product:
              registration.product.cached_short_name ||
              registration.product.cached_long_name ||
              "Registered product",
            variant:
              registration.product.product_type
                ?.replace(/_/g, " ")
                .toLowerCase() || "Product",
            warrantyClass: className || "Warranty",
            serialNumber: identity?.value ?? "",
            serialLabel: identity?.label ?? "",
            reference: formatRegistrationReference(registration),
            paymentState: registrationPaymentWorkflowState,
            paymentWorkflowInstanceUuid:
              registrationPaymentWorkflowState?.instance_uuid,
            status: registrationStatusLabel,
            statusKey: registrationStatusKey,
            statusNeedsAttention: registrationStatusNeedsAttention,
            canOpenRecord: true,
            type: warrantyType || "Standard",
            term: warranty?.is_lifetime
              ? "Lifetime"
              : formatDurationFromMonths(warranty?.duration_in_months) || "-",
            installed: installedDate
              ? formatDate(installedDate) || installedDate
              : "-",
            needsAction: registrationNeedsAction,
            issueUuid: primaryIssueUuid,
            statusWorkflowInstanceUuid:
              registrationWorkflowState?.instance_uuid,
          };
        });
      },
    [formatDate, identityCodes, structureType],
  );

  const propertyRows = useMemo<PropertyRecordWarrantyRow[]>(
    () =>
      propertyHoldings.flatMap((registration) =>
        buildWarrantyRows(registration),
      ),
    [buildWarrantyRows, propertyHoldings],
  );

  const structureGroups = useMemo<PropertyStructureGroup[]>(() => {
    const groups = new Map<string, PropertyStructureGroup>();
    const addStructureGroup = (
      uuid: string,
      structureSummary: StructureSummary | null,
      fallbackName?: string,
    ) => {
      if (groups.has(uuid)) return;
      const display = getStructureDisplay(structureSummary, structureType, t);
      groups.set(uuid, {
        uuid,
        name:
          display.name ||
          fallbackName ||
          t("registrations.mainStructure", "Main structure"),
        meta: display.meta,
        sqft: display.sqft,
        built: display.built,
        rows: [],
      });
    };

    for (const propertyStructure of propertyStructures) {
      addStructureGroup(propertyStructure.uuid, propertyStructure);
    }
    if (propertyStructures.length === 0 && structure) {
      addStructureGroup(structure.uuid, structure, structureName);
    }

    for (const row of propertyRows) {
      const groupUuid =
        row.structureUuid ?? `registration-${row.holdingUuid}-${row.structure}`;
      addStructureGroup(groupUuid, null, row.structure);
      const group = groups.get(groupUuid);
      if (!group) continue;
      if (
        row.structure &&
        !isOtherStructureValue(row.structure) &&
        (isOtherStructureValue(group.name) || isOtherStructureValue(group.meta))
      ) {
        group.name = row.structure;
      }
      group.rows.push(row);
    }

    return [...groups.values()];
  }, [
    propertyRows,
    propertyStructures,
    structure,
    structureName,
    structureType,
    t,
  ]);

  const filterRows = useCallback(
    (sourceRows: PropertyRecordWarrantyRow[]) => {
      const term = search.trim().toLowerCase();
      return sourceRows.filter((row) => {
        if (filter === "ACTIVE" && row.statusKey !== "active") return false;
        if (filter === "NEEDS ACTION" && !row.needsAction) return false;
        if (!term) return true;
        return [
          row.product,
          row.variant,
          row.serialNumber,
          row.warrantyClass,
          row.reference,
          row.structure,
        ].some((value) => value.toLowerCase().includes(term));
      });
    },
    [filter, search],
  );

  const filteredPropertyRows = useMemo(
    () => filterRows(propertyRows),
    [filterRows, propertyRows],
  );
  const filteredStructureGroups = useMemo(() => {
    const term = search.trim().toLowerCase();
    return structureGroups
      .map((group) => {
        const statusRows = group.rows.filter((row) => {
          if (filter === "ACTIVE" && row.statusKey !== "active") return false;
          if (filter === "NEEDS ACTION" && !row.needsAction) return false;
          return true;
        });
        const structureMatches =
          !term ||
          [group.name, group.meta, group.sqft, group.built]
            .filter(Boolean)
            .some((value) => value.toLowerCase().includes(term));
        const rows = structureMatches ? statusRows : filterRows(statusRows);
        return { ...group, rows };
      })
      .filter((group) => {
        if (group.rows.length > 0) return true;
        if (filter !== "ALL") return false;
        if (!term) return true;
        return [group.name, group.meta, group.sqft, group.built]
          .filter(Boolean)
          .some((value) => value.toLowerCase().includes(term));
      });
  }, [filter, filterRows, search, structureGroups]);

  const visibleStructureWarrantyCount = filteredStructureGroups.reduce(
    (total, group) => total + group.rows.length,
    0,
  );

  const propertyNeedsActionCount = propertyRows.filter(
    (row) => row.needsAction,
  ).length;
  const propertyActiveCount = propertyRows.filter(
    (row) => row.statusKey === "active",
  ).length;
  // Order runs from the record itself out to its paper trail: what is covered
  // (Structures, Warranties), what has happened to it (Claims), then the
  // evidence and commentary (Documents, Notes) and finally the audit trail.
  // Notes sits beside Activity on purpose - one is the record's human trail,
  // the other its system trail, and staff reach for them together.
  // `key` is the stable identity the view switches on; `label` is what the
  // reader sees. Split so the labels can be translated without the comparisons
  // below (activeTab === "Notes") becoming locale-dependent.
  const tabs = [
    { key: "Overview", label: t("registrations.tab.overview", "Overview") },
    {
      key: "Structures",
      label: t("registrations.tab.structures", "Structures"),
    },
    {
      key: "Warranties",
      label: t("registrations.tab.warranties", "Warranties"),
    },
    { key: "Claims", label: t("registrations.tab.claims", "Claims") },
    { key: "Documents", label: t("registrations.tab.documents", "Documents") },
    { key: "Notes", label: t("registrations.tab.notes", "Notes") },
    { key: "Activity", label: t("registrations.tab.activity", "Activity") },
  ];
  const activeTabLabel =
    tabs.find((tab) => tab.key === activeTab)?.label ?? activeTab;

  return (
    <main className="min-h-full bg-[var(--bg)]">
      <div className="border-b-[0.5px] border-[var(--rule)]">
        <div className="page-container flex h-11 items-center gap-7 lg:pl-32">
          {tabs.map((tab) => (
            <Button
              key={tab.key}
              type="button"
              variant="ghost"
              onClick={() => setActiveTab(tab.key)}
              className={`h-11 rounded-none border-x-0 border-t-0 bg-transparent px-0 text-[11px] font-normal uppercase tracking-[0.14em] hover:bg-transparent ${
                activeTab === tab.key
                  ? "border-b-[1.5px] border-b-[var(--ink)] text-[var(--ink)]"
                  : "border-b-[1.5px] border-b-transparent text-[var(--ink-3)]"
              }`}
            >
              {tab.label}
            </Button>
          ))}
        </div>
      </div>

      <div className="page-container pb-20 pt-8 lg:pl-32">
        <Button
          type="button"
          variant="ghost"
          onClick={onBack}
          className="mb-5 h-auto gap-1 bg-transparent p-0 text-[11px] font-normal uppercase tracking-[0.1em] text-[var(--ink-3)] hover:bg-transparent hover:text-[var(--ink)]"
        >
          <ArrowLeft className="h-3 w-3" />
          {backLabel}
        </Button>

        <section className="flex items-end justify-between gap-6">
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <h1 className="m-0 font-display text-[44px] font-light leading-[1.05] tracking-[-0.01em] text-[var(--ink)]">
                {addressTitle}
              </h1>
            </div>
            {cityLine && (
              <div className="mt-1.5 text-[13px] font-light text-[var(--ink-3)]">
                {cityLine}
              </div>
            )}
            <div className="mt-2.5 flex flex-wrap items-center gap-2.5 text-[12px] text-[var(--ink-3)]">
              <span>
                Registered{" "}
                {holding.created_at
                  ? formatDate(holding.created_at) || holding.created_at
                  : "-"}
              </span>
              <span className="text-[var(--rule-strong)]">·</span>
              <span>
                {propertyRows.length}{" "}
                {propertyRows.length === 1 ? "record" : "records"} across{" "}
                {structureGroups.length}{" "}
                {structureGroups.length === 1 ? "structure" : "structures"}
              </span>
              {propertyNeedsActionCount > 0 && (
                <>
                  <span className="text-[var(--rule-strong)]">·</span>
                  <span className="text-[var(--gold-ink)]">
                    {propertyNeedsActionCount} need
                    {propertyNeedsActionCount === 1 ? "s" : ""} action
                  </span>
                </>
              )}
            </div>
          </div>
          <div className="flex shrink-0 items-center gap-2">
            <StatusBadge status={status} />
            <ConfigHelp
              sections={[
                "display.reg_card",
                "display.purchase_card",
                "display.warranty_card",
              ]}
              returnLabel="Registration"
              params={{
                warrantyTemplateUuid:
                  holding.warranties?.[0]?.warranty_template?.uuid ?? null,
              }}
              size="icon-lg"
              className="border-[0.5px] border-[var(--rule-strong)] hover:bg-[var(--bg-subtle)]"
            />
            {/* The property's own actions. A property record has no Host card -
                the address IS the heading - so the menu belongs here, and this
                is the counterpart of the mount on the vehicle record. Without
                it a property tenant could correct nothing about the property
                itself, while a vehicle tenant could correct the plate. Its
                edit is the platform pen, last in the header after the menu. */}
            {host?.uuid ? (
              <EntityCardActions
                entityTypeKey="host"
                editActionKey={EDIT_HOST_ACTION_KEY}
                resourceUuid={host.uuid}
                triggerLabel={t(
                  'registrations.moreHostActions',
                  'More {{noun}} actions',
                  { noun: hostNoun.toLowerCase() },
                )}
                triggerTestId="registration-host-menu"
                triggerClassName="h-7 w-7 rounded-[4px] border-[var(--rule)] bg-[var(--bg)] text-[var(--ink-3)] hover:bg-[var(--bg-subtle)]"
                onActionComplete={() => {
                  void queryClient.invalidateQueries({
                    queryKey: ["holding", holding.uuid],
                  });
                  void queryClient.invalidateQueries({
                    queryKey: ["holdings"],
                  });
                }}
              />
            ) : null}
          </div>
        </section>

        <section className="mt-5 flex flex-wrap gap-x-16 gap-y-5">
          <PropertyRecordSummaryField label="Current owner">
            {/* Through to the customer record, where their details are
                edited and their change history lives. Without it this block
                is a dead end - the owner is named here but there is no
                route to the record. */}
            <Button
              variant="link"
              onClick={() => navigate(`/customers/${consumer.uuid}`)}
              data-testid="registration-owner-link"
              className="h-auto justify-start p-0 text-[13.5px] font-light text-[var(--ink-2)] hover:text-[var(--green)]"
            >
              {ownerName}
            </Button>
            <div className="mt-[3px] text-[11.5px] text-[var(--ink-3)]">
              Since{" "}
              {holding.activated_at || holding.created_at
                ? formatDate(holding.activated_at || holding.created_at) ||
                  holding.activated_at ||
                  holding.created_at
                : "-"}
            </div>
          </PropertyRecordSummaryField>
          <PropertyRecordSummaryField label="Contact">
            <div className="text-[13.5px] font-light text-[var(--ink-2)]">
              {ownerName}
              {consumer.email && (
                <>
                  <span className="text-[var(--ink-3)]"> · </span>
                  <a
                    href={`mailto:${consumer.email}`}
                    className="text-[var(--ink-2)] no-underline hover:text-[var(--ink)]"
                  >
                    {consumer.email}
                  </a>
                </>
              )}
            </div>
            {consumer.phone_number && (
              <div className="mt-[3px] font-mono text-[11.5px] text-[var(--ink-3)]">
                {formatPhoneDisplay(consumer.phone_number)}
              </div>
            )}
          </PropertyRecordSummaryField>
          {/* The registrant's own email, kept off the owner: it gets a copy
              of the registration documents. */}
          {holding.registered_by_email && (
            <PropertyRecordSummaryField
              label={t("registrations.registeredBy", "Registered by")}
            >
              <a
                href={`mailto:${holding.registered_by_email}`}
                className="text-[13.5px] font-light text-[var(--ink-2)] no-underline hover:text-[var(--ink)]"
              >
                {holding.registered_by_email}
              </a>
            </PropertyRecordSummaryField>
          )}
        </section>

        {activeTab === "Structures" ? (
          <>
            <section className="mt-7 flex flex-wrap items-center gap-3">
              <div className="flex items-center gap-2">
                {(["ALL", "ACTIVE", "NEEDS ACTION"] as const).map((item) => (
                  <Button
                    key={item}
                    type="button"
                    variant="outline"
                    onClick={() => setFilter(item)}
                    className={cn(
                      "h-7 rounded-[4px] px-3 text-[10.5px] font-normal uppercase tracking-[0.12em]",
                      filter === item
                        ? item === "NEEDS ACTION"
                          ? "border-[var(--gold-border)] bg-[var(--gold-bg)] text-[var(--gold-ink)]"
                          : "border-[var(--green-border)] bg-[var(--green-bg)] text-[var(--green)]"
                        : "border-[var(--rule)] bg-transparent text-[var(--ink-3)]",
                    )}
                  >
                    {item}
                    {item === "NEEDS ACTION" &&
                      propertyNeedsActionCount > 0 && (
                        <span className="ml-1 rounded-full border-[0.5px] border-[var(--gold-border)] bg-[var(--bg)] px-1.5 font-mono text-[10px] text-[var(--gold-ink)]">
                          {propertyNeedsActionCount}
                        </span>
                      )}
                  </Button>
                ))}
                <Button
                  type="button"
                  variant="outline"
                  size="icon-sm"
                  onClick={() => setAddStructureOpen(true)}
                  className="h-7 w-7 rounded-[4px] border-[var(--rule)] bg-transparent text-[var(--ink-3)]"
                >
                  <Plus className="h-3 w-3" />
                </Button>
              </div>
              <div className="flex-1" />
              <div className="flex items-center gap-2">
                <PropertyToolbarButton icon={<Filter className="h-3 w-3" />}>
                  Filter
                </PropertyToolbarButton>
                <PropertyToolbarButton icon={<Columns3 className="h-3 w-3" />}>
                  Columns
                </PropertyToolbarButton>
                <div className="flex h-8 w-[240px] items-center gap-2 rounded-[4px] border-[0.5px] border-[var(--input-border)] bg-[var(--bg)] px-3">
                  <Search className="h-3 w-3 text-[var(--ink-3)]" />
                  <Input
                    value={search}
                    onChange={(event) => setSearch(event.target.value)}
                    placeholder="Search warranties..."
                    className="h-auto min-w-0 flex-1 border-0 bg-transparent p-0 text-[12.5px] font-light text-[var(--ink)] shadow-none focus-visible:ring-0"
                  />
                </div>
                <PropertyToolbarButton icon={<Download className="h-3 w-3" />}>
                  Export
                </PropertyToolbarButton>
                <div className="ml-1 inline-flex h-8 rounded-[4px] border-[0.5px] border-[var(--rule)]">
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon-sm"
                    onClick={() => setView("rows")}
                    className={`h-full w-8 rounded-none border-y-0 border-l-0 border-r-[0.5px] border-r-[var(--rule)] bg-transparent ${
                      view === "rows"
                        ? "text-[var(--ink)]"
                        : "text-[var(--ink-3)]"
                    }`}
                  >
                    <Rows3 className="h-3 w-3" />
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon-sm"
                    onClick={() => setView("grid")}
                    className={`h-full w-8 rounded-none bg-transparent ${
                      view === "grid"
                        ? "text-[var(--ink)]"
                        : "text-[var(--ink-3)]"
                    }`}
                  >
                    <Grid3X3 className="h-3 w-3" />
                  </Button>
                </div>
              </div>
            </section>

            <section className="mt-6 flex flex-col gap-7">
              {propertyHoldingsLoading || propertyStructuresLoading ? (
                <div className="rounded-[6px] border-[0.5px] border-[var(--rule-light)] px-4 py-[60px] text-center text-[13px] text-[var(--ink-3)]">
                  Loading property structures...
                </div>
              ) : (
                filteredStructureGroups.map((group) => {
                  const isStructureOpen =
                    structureOpenByUuid[group.uuid] ?? true;
                  const groupNeedsActionCount = group.rows.filter(
                    (row) => row.needsAction,
                  ).length;
                  const groupActiveCount = group.rows.filter(
                    (row) => row.statusKey === "active",
                  ).length;
                  const targetStructure = propertyStructures.find(
                    (item) => item.uuid === group.uuid,
                  );
                  const addProductToStructure = () =>
                    startRegistrationForProperty(
                      targetStructure?.attribute_values ?? {},
                      group.uuid,
                    );

                  return (
                    <div
                      key={group.uuid}
                      className="-mx-4 overflow-hidden rounded-[8px] border-[0.5px] border-[var(--rule)]"
                    >
                      <div className="flex items-center gap-3.5 bg-[var(--bg-subtle)] px-4 py-3.5">
                        <Button
                          type="button"
                          variant="ghost"
                          onClick={() =>
                            setStructureOpenByUuid((current) => ({
                              ...current,
                              [group.uuid]: !isStructureOpen,
                            }))
                          }
                          className="flex h-auto min-w-0 flex-1 justify-start gap-3.5 rounded-none bg-transparent p-0 text-left hover:bg-transparent"
                        >
                          <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-[4px] border-[0.5px] border-[var(--rule)] bg-[var(--bg)] text-[var(--ink-3)]">
                            <Home className="h-[15px] w-[15px]" />
                          </span>
                          <span className="min-w-0 flex-1 text-left">
                            <span className="block font-display text-[22px] font-light leading-[1.1] text-[var(--ink)]">
                              {group.name}
                            </span>
                            <span className="mt-1 block text-[11.5px] font-light text-[var(--ink-3)]">
                              {[
                                group.meta,
                                group.sqft,
                                group.built && `built ${group.built}`,
                              ]
                                .filter(Boolean)
                                .join(" · ")}
                            </span>
                          </span>
                        </Button>
                        <div className="flex items-center gap-3.5 text-[11.5px] text-[var(--ink-3)]">
                          {groupNeedsActionCount > 0 && (
                            <PropertyRecordPill tone="warn">
                              {groupNeedsActionCount} need
                              {groupNeedsActionCount === 1 ? "s" : ""} action
                            </PropertyRecordPill>
                          )}
                          <span className="font-mono">
                            {groupActiveCount} active
                          </span>
                          {can("transfers.create") && groupActiveCount > 0 && (
                            <Button
                              type="button"
                              variant="outline"
                              size="xs"
                              onClick={() =>
                                navigate(
                                  `/transfers/new?structure=${group.uuid}`,
                                )
                              }
                              title="Start a transfer for warranties on this structure"
                              className="h-[26px] rounded-[4px] border-[0.5px] border-[var(--rule)] bg-[var(--bg)] px-3 text-[10.5px] font-normal uppercase tracking-[0.1em] text-[var(--ink-2)] hover:border-[var(--ink-3)] hover:bg-[var(--bg)] hover:text-[var(--ink)]"
                            >
                              Transfer
                            </Button>
                          )}
                          <CardActionMenu
                            triggerLabel={`More ${group.name} actions`}
                            triggerClassName="h-[26px] w-[26px] border-0 bg-transparent text-[var(--ink-3)] hover:bg-[var(--bg)]"
                            contentClassName="min-w-[230px]"
                            items={structureMenuItems(group)}
                          />
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon-sm"
                            onClick={() =>
                              setStructureOpenByUuid((current) => ({
                                ...current,
                                [group.uuid]: !isStructureOpen,
                              }))
                            }
                            className="h-5 w-5 bg-transparent p-0 text-[var(--ink-3)] hover:bg-transparent"
                          >
                            <ChevronDown
                              className={`h-3 w-3 transition-transform ${
                                isStructureOpen ? "" : "-rotate-90"
                              }`}
                            />
                          </Button>
                        </div>
                      </div>

                      {isStructureOpen && (
                        <>
                          <div className="grid grid-cols-[48px_minmax(200px,1.2fr)_minmax(170px,0.95fr)_120px_minmax(130px,0.85fr)_90px_90px_110px_120px] border-b-[0.5px] border-b-[var(--rule)]">
                            <PropertyRecordHeaderCell />
                            <PropertyRecordHeaderCell>
                              Product
                            </PropertyRecordHeaderCell>
                            <PropertyRecordHeaderCell>
                              Warranty class · Reg #
                            </PropertyRecordHeaderCell>
                            <PropertyRecordHeaderCell>
                              Payment
                            </PropertyRecordHeaderCell>
                            <PropertyRecordHeaderCell>
                              Status
                            </PropertyRecordHeaderCell>
                            <PropertyRecordHeaderCell>
                              Type
                            </PropertyRecordHeaderCell>
                            <PropertyRecordHeaderCell align="right">
                              Term
                            </PropertyRecordHeaderCell>
                            <PropertyRecordHeaderCell align="right">
                              Installed
                            </PropertyRecordHeaderCell>
                            <PropertyRecordHeaderCell align="right" />
                          </div>
                          {group.rows.map((row) => (
                            <div
                              key={`${row.holdingUuid}-${row.uuid}`}
                              role={row.canOpenRecord ? "button" : undefined}
                              tabIndex={row.canOpenRecord ? 0 : undefined}
                              onClick={
                                row.canOpenRecord
                                  ? () => openWarrantyRecord(row)
                                  : undefined
                              }
                              onKeyDown={
                                row.canOpenRecord
                                  ? (event) =>
                                      handleWarrantyRowKeyDown(event, row)
                                  : undefined
                              }
                              className={cn(
                                "grid grid-cols-[48px_minmax(200px,1.2fr)_minmax(170px,0.95fr)_120px_minmax(130px,0.85fr)_90px_90px_110px_120px] border-b-[0.5px] border-b-[var(--rule-light)] transition-colors",
                                row.canOpenRecord
                                  ? "cursor-pointer hover:bg-[var(--bg-subtle)] focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-[var(--ink)]"
                                  : "cursor-default",
                              )}
                            >
                              <PropertyRecordCell>
                                <span className="ml-2 flex h-6 w-6 items-center justify-center rounded-[4px] border-[0.5px] border-[var(--rule-light)] bg-[var(--bg-subtle)] text-[var(--ink-3)]">
                                  <Shield className="h-3 w-3" />
                                </span>
                              </PropertyRecordCell>
                              <PropertyRecordCell>
                                <PropertyRecordProductCell row={row} />
                              </PropertyRecordCell>
                              <PropertyRecordCell>
                                <div className="text-[12.5px] font-light text-[var(--ink-2)]">
                                  {row.warrantyClass}
                                </div>
                                <div className="mt-0.5 font-mono text-[11px] text-[var(--ink-3)]">
                                  REG # {row.reference}
                                </div>
                              </PropertyRecordCell>
                              <PropertyRecordCell>
                                {row.paymentState ? (
                                  <WorkflowStateBadge
                                    workflowKey={row.paymentState.workflow_key}
                                    stateKey={row.paymentState.state_key}
                                    label={
                                      row.paymentState.state_label ||
                                      row.paymentState.state_key
                                    }
                                    color={row.paymentState.state_color}
                                    isTerminal={row.paymentState.is_terminal}
                                  />
                                ) : (
                                  <span className="text-[11px] text-[var(--ink-3)]">
                                    -
                                  </span>
                                )}
                              </PropertyRecordCell>
                              <PropertyRecordCell>
                                <StatusBadge
                                  status={row.status}
                                  icon={
                                    row.statusNeedsAttention ? (
                                      <AlertTriangle className="h-3 w-3 text-[var(--ink-3)]" />
                                    ) : undefined
                                  }
                                  tooltip={
                                    row.statusNeedsAttention
                                      ? "Approval required before this registration can go live."
                                      : undefined
                                  }
                                />
                              </PropertyRecordCell>
                              <PropertyRecordCell>
                                {row.type}
                              </PropertyRecordCell>
                              <PropertyRecordCell align="right" muted>
                                {row.term}
                              </PropertyRecordCell>
                              <PropertyRecordCell align="right">
                                {row.installed}
                              </PropertyRecordCell>
                              <PropertyRecordCell align="right">
                                <div onClick={(e) => e.stopPropagation()}>
                                  <EntityActionMenu
                                    entityTypeKey="holding"
                                    resourceUuid={row.holdingUuid}
                                    triggerClassName="h-7 w-7 rounded-[4px] border-[var(--rule)] bg-[var(--bg)] text-[var(--ink-3)] hover:bg-[var(--bg-subtle)]"
                                    contentClassName="min-w-[210px]"
                                    extraItems={recordMenuExtraItems(row)}
                                  />
                                </div>
                              </PropertyRecordCell>
                            </div>
                          ))}
                          {group.rows.length === 0 && (
                            <div className="border-b-[0.5px] border-b-[var(--rule-light)] px-4 py-8 text-center">
                              <div className="text-[13px] text-[var(--ink-3)]">
                                No products or warranties attached to this
                                structure yet.
                              </div>
                            </div>
                          )}
                          <div className="bg-[var(--bg-subtle)] px-4 py-2.5">
                            <Button
                              type="button"
                              variant="outline"
                              onClick={addProductToStructure}
                              className="h-7 rounded-[4px] border-dashed border-[var(--rule-strong)] bg-transparent px-3 text-[11.5px] font-light normal-case tracking-normal text-[var(--ink-2)] hover:bg-[var(--bg)]"
                            >
                              <Plus className="h-3 w-3" />
                              Add product to {group.name.toLowerCase()}
                            </Button>
                          </div>
                        </>
                      )}
                    </div>
                  );
                })
              )}
              {!canViewStructures ? (
                <div className="rounded-[6px] border-[0.5px] border-[var(--rule-light)] px-4 py-[60px] text-center text-[13px] text-[var(--ink-3)]">
                  You do not have permission to view{" "}
                  {structureEntity?.label ?? "these"} records.
                </div>
              ) : (
                !propertyHoldingsLoading &&
                !propertyStructuresLoading &&
                filteredStructureGroups.length === 0 && (
                  <div className="rounded-[6px] border-[0.5px] border-[var(--rule-light)] px-4 py-[60px] text-center text-[13px] text-[var(--ink-3)]">
                    No structures match these filters.
                  </div>
                )
              )}
              {canCreateStructure ? (
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setAddStructureOpen(true)}
                  className="h-8 w-fit rounded-[4px] border-dashed border-[var(--rule-strong)] bg-transparent px-3.5 text-[12px] font-light normal-case tracking-normal text-[var(--ink-2)]"
                >
                  <Plus className="h-3 w-3" />
                  Add {structureEntity?.label?.toLowerCase() ?? "record"}
                </Button>
              ) : null}
            </section>

            <footer className="mt-3.5 flex items-center justify-between text-[11.5px] text-[var(--ink-3)]">
              <span>
                Showing {visibleStructureWarrantyCount} of {propertyRows.length}{" "}
                records across {filteredStructureGroups.length}{" "}
                {filteredStructureGroups.length === 1
                  ? "structure"
                  : "structures"}
              </span>
              <span>
                {formatRegistrationReference(holding)} · Registered{" "}
                {holding.created_at
                  ? formatDate(holding.created_at) || holding.created_at
                  : "-"}{" "}
                · Owner {ownerName}
              </span>
            </footer>
          </>
        ) : activeTab === "Warranties" ? (
          <>
            <section className="mt-7 flex flex-wrap items-center gap-3">
              <div className="flex items-center gap-2">
                {(["ALL", "ACTIVE", "NEEDS ACTION"] as const).map((item) => (
                  <Button
                    key={item}
                    type="button"
                    variant="outline"
                    onClick={() => setFilter(item)}
                    className={cn(
                      "h-7 rounded-[4px] px-3 text-[10.5px] font-normal uppercase tracking-[0.12em]",
                      filter === item
                        ? item === "NEEDS ACTION"
                          ? "border-[var(--gold-border)] bg-[var(--gold-bg)] text-[var(--gold-ink)]"
                          : "border-[var(--green-border)] bg-[var(--green-bg)] text-[var(--green)]"
                        : "border-[var(--rule)] bg-transparent text-[var(--ink-3)]",
                    )}
                  >
                    {item}
                    {item === "NEEDS ACTION" &&
                      propertyNeedsActionCount > 0 && (
                        <span className="ml-1 rounded-full border-[0.5px] border-[var(--gold-border)] bg-[var(--bg)] px-1.5 font-mono text-[10px] text-[var(--gold-ink)]">
                          {propertyNeedsActionCount}
                        </span>
                      )}
                  </Button>
                ))}
              </div>
              <div className="flex-1" />
              <div className="flex items-center gap-2">
                <PropertyToolbarButton icon={<Filter className="h-3 w-3" />}>
                  Filter
                </PropertyToolbarButton>
                <PropertyToolbarButton icon={<Columns3 className="h-3 w-3" />}>
                  Columns
                </PropertyToolbarButton>
                <div className="flex h-8 w-[240px] items-center gap-2 rounded-[4px] border-[0.5px] border-[var(--input-border)] bg-[var(--bg)] px-3">
                  <Search className="h-3 w-3 text-[var(--ink-3)]" />
                  <Input
                    value={search}
                    onChange={(event) => setSearch(event.target.value)}
                    placeholder="Search warranties..."
                    className="h-auto min-w-0 flex-1 border-0 bg-transparent p-0 text-[12.5px] font-light text-[var(--ink)] shadow-none focus-visible:ring-0"
                  />
                </div>
                <PropertyToolbarButton icon={<Download className="h-3 w-3" />}>
                  Export
                </PropertyToolbarButton>
              </div>
            </section>

            <section className="mt-6">
              <div className="mb-3 flex items-center justify-between text-[11.5px] text-[var(--ink-3)]">
                <span>
                  {propertyRows.length}{" "}
                  {propertyRows.length === 1 ? "warranty" : "warranties"} on
                  this property
                </span>
                <span className="font-mono">{propertyActiveCount} active</span>
              </div>
              <div className="grid grid-cols-[minmax(150px,0.8fr)_minmax(200px,1.1fr)_minmax(170px,0.95fr)_120px_minmax(130px,0.85fr)_90px_90px_110px_120px] border-b-[0.5px] border-b-[var(--rule)]">
                <PropertyRecordHeaderCell>Structure</PropertyRecordHeaderCell>
                <PropertyRecordHeaderCell>Product</PropertyRecordHeaderCell>
                <PropertyRecordHeaderCell>
                  Warranty class · Reg #
                </PropertyRecordHeaderCell>
                <PropertyRecordHeaderCell>Payment</PropertyRecordHeaderCell>
                <PropertyRecordHeaderCell>Status</PropertyRecordHeaderCell>
                <PropertyRecordHeaderCell>Type</PropertyRecordHeaderCell>
                <PropertyRecordHeaderCell align="right">
                  Term
                </PropertyRecordHeaderCell>
                <PropertyRecordHeaderCell align="right">
                  Installed
                </PropertyRecordHeaderCell>
                <PropertyRecordHeaderCell align="right" />
              </div>
              {propertyHoldingsLoading ? (
                <div className="rounded-[6px] border-[0.5px] border-[var(--rule-light)] px-4 py-[60px] text-center text-[13px] text-[var(--ink-3)]">
                  Loading property warranties...
                </div>
              ) : (
                filteredPropertyRows.map((row) => (
                  <div
                    key={`${row.holdingUuid}-${row.uuid}`}
                    role="button"
                    tabIndex={0}
                    onClick={() => openWarrantyRecord(row)}
                    onKeyDown={(event) => handleWarrantyRowKeyDown(event, row)}
                    className="grid cursor-pointer grid-cols-[minmax(150px,0.8fr)_minmax(200px,1.1fr)_minmax(170px,0.95fr)_120px_minmax(130px,0.85fr)_90px_90px_110px_120px] border-b-[0.5px] border-b-[var(--rule-light)] transition-colors hover:bg-[var(--bg-subtle)] focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-[var(--ink)]"
                  >
                    <PropertyRecordCell>
                      <div className="text-[12.5px] font-light text-[var(--ink-2)]">
                        {row.structure}
                      </div>
                    </PropertyRecordCell>
                    <PropertyRecordCell>
                      <PropertyRecordProductCell row={row} />
                    </PropertyRecordCell>
                    <PropertyRecordCell>
                      <div className="text-[12.5px] font-light text-[var(--ink-2)]">
                        {row.warrantyClass}
                      </div>
                      <div className="mt-0.5 font-mono text-[11px] text-[var(--ink-3)]">
                        REG # {row.reference}
                      </div>
                    </PropertyRecordCell>
                    <PropertyRecordCell>
                      {row.paymentState ? (
                        <WorkflowStateBadge
                          workflowKey={row.paymentState.workflow_key}
                          stateKey={row.paymentState.state_key}
                          label={
                            row.paymentState.state_label ||
                            row.paymentState.state_key
                          }
                          color={row.paymentState.state_color}
                          isTerminal={row.paymentState.is_terminal}
                        />
                      ) : (
                        <span className="text-[11px] text-[var(--ink-3)]">
                          -
                        </span>
                      )}
                    </PropertyRecordCell>
                    <PropertyRecordCell>
                      <PropertyRecordPill
                        tone={propertyRecordStatusTone(row.statusKey)}
                      >
                        {row.status}
                      </PropertyRecordPill>
                    </PropertyRecordCell>
                    <PropertyRecordCell>{row.type}</PropertyRecordCell>
                    <PropertyRecordCell align="right" muted>
                      {row.term}
                    </PropertyRecordCell>
                    <PropertyRecordCell align="right">
                      {row.installed}
                    </PropertyRecordCell>
                    <PropertyRecordCell align="right">
                      <div onClick={(e) => e.stopPropagation()}>
                        <EntityActionMenu
                          entityTypeKey="holding"
                          resourceUuid={row.holdingUuid}
                          triggerClassName="h-7 w-7 rounded-[4px] border-[var(--rule)] bg-[var(--bg)] text-[var(--ink-3)] hover:bg-[var(--bg-subtle)]"
                          contentClassName="min-w-[210px]"
                          extraItems={recordMenuExtraItems(row)}
                        />
                      </div>
                    </PropertyRecordCell>
                  </div>
                ))
              )}
              {!propertyHoldingsLoading &&
                filteredPropertyRows.length === 0 && (
                  <div className="rounded-[6px] border-[0.5px] border-[var(--rule-light)] px-4 py-[60px] text-center text-[13px] text-[var(--ink-3)]">
                    No warranties match these filters.
                  </div>
                )}
            </section>

            <footer className="mt-3.5 flex items-center justify-between text-[11.5px] text-[var(--ink-3)]">
              <span>
                Showing {filteredPropertyRows.length} of {propertyRows.length}{" "}
                warranties
              </span>
              <span>
                {formatRegistrationReference(holding)} · Registered{" "}
                {holding.created_at
                  ? formatDate(holding.created_at) || holding.created_at
                  : "-"}{" "}
                · Owner {ownerName}
              </span>
            </footer>
          </>
        ) : activeTab === "Notes" ? (
          // The roll-up, not an author surface: notes are written on the
          // product they concern (see the warranty record) so the writer never
          // leaves the thing they are describing, and read back here so nobody
          // has to open every product to learn what has been said.
          <PropertyNotesTab structureGroups={structureGroups} />
        ) : activeTab === "Activity" ? (
          <PropertyActivityTab
            holding={holding}
            propertyHoldings={propertyHoldings}
          />
        ) : activeTab === "Overview" && !isPartnerAccess ? (
          // What this registration has sent, and Create New to send a letter
          // or email from it (WPS-231). Same card as a claim and the standard
          // registration record; the team's record, so partners never send.
          <div className="mt-8">
            <RecordCommunicationsCard
              recordType="holding"
              recordUuid={holding.uuid}
              testIdPrefix="registration-comms"
              emptyLabel={t(
                "registrations.comms.empty",
                "Nothing has been sent on this registration yet.",
              )}
              letterHint={t(
                "registrations.comms.letterHint",
                "A formal letter, kept on the registration as a PDF",
              )}
            />
          </div>
        ) : activeTab === "Documents" ? (
          <div className="mt-8">
            {propertyHostUuid ? (
              <AttachmentsCard
                // Scoped to the property, not to the one registration record
                // whose uuid is in the URL. This page is property-level -
                // several records share an address, and all of them render
                // this same view - so per-record documents would read as
                // documents vanishing between identical-looking pages. The
                // host adapter folds each record's own documents into this
                // list, so nothing captured per record is lost.
                subjectType="host"
                subjectUuid={propertyHostUuid}
                // Registration documents are a mixed bag - contracts,
                // inspection reports, receipts - so the broadest document use
                // case applies. What a file actually is lives in its free-text
                // label (`category`), not in `file_purpose`. Every document
                // use case shares one MIME allowlist, so this only affects how
                // the row reads back in audit and reporting.
                useCase="WARRANTY_DOCUMENT"
                title={t("registrations.tab.documents", "Documents")}
                icon={<FileText className="h-3.5 w-3.5" />}
                canAdd={canManageDocuments}
                canDelete={canManageDocuments}
                canRelabel={
                  canManageDocuments || can("attachments.recategorise")
                }
                labelPlaceholder={t(
                  "registrations.documentsLabelPlaceholder",
                  "e.g. Proof of purchase",
                )}
                emptyText={t(
                  "registrations.documentsEmpty",
                  "No documents attached to this property yet.",
                )}
              />
            ) : (
              <div className="rounded-[6px] border-[0.5px] border-[var(--rule-light)] px-5 py-[60px] text-center text-[13px] text-[var(--ink-3)]">
                {t(
                  "registrations.noPropertyOnRecord",
                  "Documents attach to the property, and this registration has no property on record yet.",
                )}
              </div>
            )}
          </div>
        ) : (
          <div className="mt-8 rounded-[6px] border-[0.5px] border-[var(--rule-light)] px-5 py-[60px] text-center text-[13px] text-[var(--ink-3)]">
            <span className="font-display text-[22px] font-light text-[var(--ink-2)]">
              {activeTabLabel}
            </span>
            <div className="mt-1.5">
              {t("registrations.tabNotAvailable", "Not part of this view yet.")}
            </div>
          </div>
        )}
      </div>
      {canCreateStructure ? (
        <EditFormDialog
          open={addStructureOpen}
          onOpenChange={setAddStructureOpen}
          title={`Add ${structureEntity?.label ?? "record"}`}
          fields={structureFields}
          values={addStructureInitialValues}
          onSave={createStructure}
          isPending={isCreatingStructure}
          submissionMode="submit"
        />
      ) : null}
    </main>
  );
}

type PropertyNoteSummaryEntry = {
  uuid: string;
  record_uuid: string;
  mentions?: { uuid: string; name: string }[];
  title: string;
  content: string;
  category: string;
  is_internal: boolean;
  created_by_name: string;
  created_at: string;
};

/**
 * Every note written on this property, read back in one place.
 *
 * Notes are authored on the product they are about (the warranty record), which
 * is where the thought occurs and where it stays useful. This tab is the other
 * half of that: the roll-up, so nobody has to open six products to find out what
 * has been said about the roof. It reads, it does not author - the "Add note"
 * affordance lives on each product, and every row here is a way back to it.
 *
 * Deliberately roomier than the rest of the record's chrome. Notes are prose
 * written by one person for another, often read by people who are not looking
 * at a screen all day, so the text is set larger than the surrounding table
 * furniture and each row is a full-size target rather than a dense line item.
 */
function PropertyNoteAvatar({ name }: { name: string }) {
  const initials =
    (name || "?")
      .split(" ")
      .filter(Boolean)
      .map((word) => word[0])
      .join("")
      .slice(0, 2)
      .toUpperCase() || "?";
  return (
    <span
      aria-hidden="true"
      className="mt-[1px] inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full border-[0.5px] border-[var(--green-border)] bg-[var(--green-bg)] text-[9px] font-normal uppercase tracking-[0.04em] text-[var(--green)]"
    >
      {initials}
    </span>
  );
}

/** One note, as it reads in the roll-up: who, when, how visible, what. */
function PropertyNoteRow({ note }: { note: PropertyNoteSummaryEntry }) {
  const { t } = useTranslation();
  return (
    // `--rule-light` (5% alpha) reads as no line at all once notes stack up -
    // the eye cannot tell where one note ends. `--rule` (14%) is the faintest
    // weight that still separates.
    <article className="flex gap-3 border-b-[0.5px] border-b-[var(--rule)] px-4 py-4 last:border-b-0">
      <PropertyNoteAvatar name={note.created_by_name} />
      <div className="flex min-w-0 flex-1 flex-col gap-1">
        <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5">
          <span className="text-[12.5px] text-[var(--ink-2)]">
            {note.created_by_name || t("timeline.system", "System")}
          </span>
          <span aria-hidden="true" className="text-[var(--rule-strong)]">
            ·
          </span>
          {/* Relative for scanning, exact on hover - five identical
              timestamps down a column is noise, "2 hours ago" is a fact. */}
          <time
            dateTime={note.created_at}
            title={formatPropertyNoteDate(note.created_at)}
            className="text-[12px] text-[var(--ink-3)]"
          >
            {formatRelativeNoteTime(note.created_at)}
          </time>
          <span
            className={cn(
              "inline-flex items-center gap-[3px] text-[11px]",
              note.is_internal ? "text-[var(--ink-3)]" : "text-[var(--green)]",
            )}
          >
            {note.is_internal ? (
              <>
                <Lock className="h-[10px] w-[10px]" />
                {t("notes.visibility.internal", "Staff only")}
              </>
            ) : (
              <>
                <Eye className="h-[10px] w-[10px]" />
                {t("notes.visibility.partnerVisible", "Shared with partner")}
              </>
            )}
          </span>
        </div>
        {note.title ? (
          <h5 className="m-0 text-[13.5px] font-medium leading-[1.45] text-[var(--ink)]">
            {note.title}
          </h5>
        ) : null}
        {/* Mentions render as chips here exactly as they do in the panel the
            note was written in - a colleague's name should not degrade to raw
            "@Anna Davidson" text just because you are reading the roll-up. */}
        <MentionedText
          text={note.content}
          mentions={note.mentions ?? []}
          className="m-0 whitespace-pre-wrap text-[14px] leading-[1.65] text-[var(--ink)]"
        />
      </div>
    </article>
  );
}

/**
 * Every note written on this property, read back in one place.
 *
 * Notes are authored on the product they are about (the warranty record), which
 * is where the thought occurs and where it stays useful. This tab is the other
 * half: the roll-up, so nobody opens six products to find out what has been
 * said about the roof. It reads; the "Add note" affordance lives on each
 * product, and every card is a way back to it.
 *
 * Three decisions shape the layout:
 *
 *  * The header answers "is there anything I need to know?" before "how many?"
 *    - a bare count cannot be acted on, "last note 2 hours ago" can.
 *  * Only the two most recent notes per product are open. A property with five
 *    notes on one product was a wall of prose with no way in; the rest sit
 *    behind a count you can expand. Progressive disclosure, not truncation -
 *    nothing is hidden, it is folded.
 *  * Products with nothing written on them are recessive and single-line, so
 *    the eye lands on what has been said, while the way to write the first
 *    note stays on screen.
 */
function PropertyNotesTab({
  structureGroups,
}: {
  structureGroups: PropertyStructureGroup[];
}) {
  const navigate = useNavigate();
  const fromClaim = useFromClaim();
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});

  // One request for the whole property rather than one per product: a property
  // with a dozen registrations would otherwise open a dozen connections just to
  // render a summary.
  const holdingUuids = useMemo(() => {
    const seen = new Set<string>();
    for (const group of structureGroups) {
      for (const row of group.rows) {
        if (row.holdingUuid) seen.add(row.holdingUuid);
      }
    }
    return Array.from(seen).sort();
  }, [structureGroups]);

  const { t } = useTranslation();
  const { data: notes = [], isLoading } = useQuery<PropertyNoteSummaryEntry[]>({
    queryKey: ["property-notes", holdingUuids],
    queryFn: async () => {
      const res = await api.get("/notes/", {
        params: {
          entity_ids: holdingUuids.join(","),
          record_type: "registration",
          page_size: 500,
        },
      });
      return (res.data?.results ??
        res.data ??
        []) as PropertyNoteSummaryEntry[];
    },
    enabled: holdingUuids.length > 0,
  });

  const notesByHolding = useMemo(() => {
    const map = new Map<string, PropertyNoteSummaryEntry[]>();
    for (const note of notes) {
      const bucket = map.get(note.record_uuid);
      if (bucket) bucket.push(note);
      else map.set(note.record_uuid, [note]);
    }
    for (const bucket of map.values()) {
      bucket.sort(
        (a, b) =>
          new Date(b.created_at).getTime() - new Date(a.created_at).getTime(),
      );
    }
    return map;
  }, [notes]);

  const openProductNotes = useCallback(
    (row: PropertyRecordWarrantyRow) => {
      navigate(
        withFromClaim(
          `/registrations/${row.holdingUuid}/warranties/${row.uuid}#notes`,
          fromClaim,
        ),
      );
    },
    [navigate, fromClaim],
  );

  // One entry per REGISTRATION, not per warranty row.
  //
  // `structureGroups` carries a row per warranty, and a registration with two
  // warranties (a standard plus an extended, say) therefore appears twice with
  // the same `holdingUuid`. Notes hang off the registration, so listing the
  // rows verbatim would print the same notes under both, double the counts and
  // tie their fold state together. Collapse to the registration and keep the
  // first row for its label and its way back into the record.
  const groups = useMemo(
    () =>
      structureGroups
        .filter((group) => group.rows.length > 0)
        .map((group) => {
          const seen = new Set<string>();
          const products: PropertyRecordWarrantyRow[] = [];
          for (const row of group.rows) {
            if (seen.has(row.holdingUuid)) continue;
            seen.add(row.holdingUuid);
            products.push(row);
          }
          return { ...group, products };
        }),
    [structureGroups],
  );
  const productCount = groups.reduce(
    (total, g) => total + g.products.length,
    0,
  );
  const notedProductCount = groups.reduce(
    (total, group) =>
      total +
      group.products.filter(
        (row) => (notesByHolding.get(row.holdingUuid) ?? []).length > 0,
      ).length,
    0,
  );
  const latestNote = useMemo(
    () =>
      notes.reduce<PropertyNoteSummaryEntry | null>(
        (latest, note) =>
          !latest ||
          new Date(note.created_at).getTime() >
            new Date(latest.created_at).getTime()
            ? note
            : latest,
        null,
      ),
    [notes],
  );

  if (isLoading) {
    return (
      <div className="mt-8 rounded-[6px] border-[0.5px] border-[var(--rule-light)] px-5 py-[60px] text-center text-[13px] text-[var(--ink-3)]">
        {t("notes.loading", "Loading notes...")}
      </div>
    );
  }

  if (groups.length === 0) {
    return (
      <div className="mt-8 rounded-[6px] border-[0.5px] border-[var(--rule-light)] px-5 py-[60px] text-center text-[13px] text-[var(--ink-3)]">
        <span className="font-display text-[22px] font-light text-[var(--ink-2)]">
          {t("notes.title", "Notes")}
        </span>
        <div className="mt-1.5">
          {t(
            "notes.propertyEmpty",
            "Register a product against this property and you can start taking notes on it.",
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="mt-8">
      {/* Header answers "anything I need to know?" before "how many?" */}
      <div className="mb-6 flex flex-wrap items-end justify-between gap-x-6 gap-y-2 border-b-[0.5px] border-b-[var(--rule)] pb-4">
        <div>
          <div className="text-[10px] font-normal uppercase tracking-[0.16em] text-[var(--ink-3)]">
            {t("notes.summaryLabel", "Summary")}
          </div>
          <h3 className="mt-1 font-display text-[24px] font-light leading-[1.2] text-[var(--ink)]">
            {t("notes.propertySummaryTitle", "Notes across this property")}
          </h3>
        </div>
        <div className="text-right">
          <div className="text-[13px] text-[var(--ink-2)]">
            {notes.length === 0
              ? t("notes.nothingWrittenYet", "Nothing written yet")
              : // "on 1 of 3" not "across 3" - the second phrasing implies all
                // three products have been written on, which is the opposite of
                // what a reader needs to know. Two keys, not one: the note
                // count and the product count pluralise independently and
                // i18next pluralises on a single `count`.
                `${t("notes.noteCount", { count: notes.length })} ${t(
                  "notes.onProducts",
                  { count: productCount, noted: notedProductCount },
                )}`}
          </div>
          {latestNote ? (
            <div className="mt-0.5 text-[12px] text-[var(--ink-3)]">
              {t("notes.lastNote", "Last note {{time}}", {
                time: formatRelativeNoteTime(latestNote.created_at),
              })}
            </div>
          ) : null}
        </div>
      </div>

      <div className="flex flex-col gap-9">
        {groups.map((group) => (
          <section key={group.uuid}>
            <div className="mb-3 flex items-baseline gap-2">
              <h4 className="m-0 text-[10px] font-normal uppercase tracking-[0.16em] text-[var(--ink-3)]">
                {group.name}
              </h4>
              {group.meta ? (
                <span className="text-[11px] text-[var(--ink-3)] opacity-70">
                  {group.meta}
                </span>
              ) : null}
            </div>

            <div className="flex flex-col gap-3">
              {group.products.map((row) => {
                const rowNotes = notesByHolding.get(row.holdingUuid) ?? [];
                // `row.variant` is the product TYPE humanised ("variant"), not a
                // name - useful as a sub-label in the warranty table, noise as a
                // heading. The reference underneath disambiguates two products
                // that share a name.
                const productLabel =
                  row.product || t("notes.productFallback", "Product");
                const isOpen = expanded[row.holdingUuid] ?? false;
                const visible = isOpen ? rowNotes : rowNotes.slice(0, 2);
                const hiddenCount = rowNotes.length - visible.length;

                // Nothing written here: one quiet line, not an empty card. The
                // eye should go to what HAS been said, with the way to write
                // the first note still in reach.
                if (rowNotes.length === 0) {
                  return (
                    <Button
                      key={`${row.holdingUuid}-${row.uuid}`}
                      type="button"
                      variant="ghost"
                      onClick={() => openProductNotes(row)}
                      aria-label={t(
                        "notes.addFirstNoteAria",
                        "Add the first note on {{product}}, {{reference}}",
                        { product: productLabel, reference: row.reference },
                      )}
                      className="flex h-auto min-h-[44px] w-full cursor-pointer items-center justify-between gap-4 rounded-[8px] border-[0.5px] border-dashed border-[var(--rule)] bg-transparent px-4 py-3 text-left hover:border-[var(--rule-strong)] hover:bg-[var(--bg-subtle)]"
                    >
                      <span className="flex min-w-0 items-baseline gap-2">
                        <span className="truncate text-[13.5px] text-[var(--ink-2)]">
                          {productLabel}
                        </span>
                        <span className="truncate text-[11.5px] text-[var(--ink-3)]">
                          {row.reference}
                        </span>
                      </span>
                      <span className="inline-flex shrink-0 items-center gap-1 text-[10px] uppercase tracking-[0.1em] text-[var(--green)]">
                        <Plus className="h-3 w-3" />
                        {t("notes.addNote", "Add note")}
                      </span>
                    </Button>
                  );
                }

                return (
                  <div
                    key={`${row.holdingUuid}-${row.uuid}`}
                    className="overflow-hidden rounded-[8px] border-[0.5px] border-[var(--rule)] bg-[var(--bg)]"
                  >
                    <div className="flex items-center justify-between gap-4 border-b-[0.5px] border-b-[var(--rule)] bg-[var(--bg-subtle)] px-4 py-3">
                      <div className="flex min-w-0 items-baseline gap-2">
                        <span className="truncate text-[14px] text-[var(--ink)]">
                          {productLabel}
                        </span>
                        <span className="truncate text-[11.5px] text-[var(--ink-3)]">
                          {row.reference}
                        </span>
                      </div>
                      <div className="flex shrink-0 items-center gap-3">
                        <span className="text-[11px] uppercase tracking-[0.1em] text-[var(--ink-3)]">
                          {t("notes.noteCount", { count: rowNotes.length })}
                        </span>
                        <Button
                          type="button"
                          variant="ghost"
                          size="xs"
                          onClick={() => openProductNotes(row)}
                          aria-label={t(
                            "notes.openProductAria",
                            "Open {{product}} to read or add notes",
                            { product: productLabel },
                          )}
                          className="inline-flex h-7 cursor-pointer items-center gap-1 bg-transparent px-0 text-[10px] font-normal uppercase tracking-[0.1em] text-[var(--green)] transition-opacity duration-150 hover:opacity-70"
                        >
                          {t("notes.open", "Open")}
                          <ArrowUpRight className="h-3 w-3" />
                        </Button>
                      </div>
                    </div>

                    {visible.map((note) => (
                      <PropertyNoteRow key={note.uuid} note={note} />
                    ))}

                    {hiddenCount > 0 || isOpen ? (
                      <div className="border-t-[0.5px] border-t-[var(--rule)] bg-[var(--bg-subtle)]">
                        <Button
                          type="button"
                          variant="ghost"
                          onClick={() =>
                            setExpanded((current) => ({
                              ...current,
                              [row.holdingUuid]: !isOpen,
                            }))
                          }
                          aria-expanded={isOpen}
                          className="flex h-auto min-h-[40px] w-full cursor-pointer items-center justify-center gap-1.5 rounded-none bg-transparent px-4 py-2.5 text-[11px] font-normal uppercase tracking-[0.1em] text-[var(--ink-3)] hover:bg-transparent hover:text-[var(--ink)]"
                        >
                          <ChevronDown
                            className={cn(
                              "h-3 w-3 transition-transform duration-150",
                              isOpen && "rotate-180",
                            )}
                          />
                          {isOpen
                            ? t("notes.showFewer", "Show fewer")
                            : t("notes.earlierNotes", { count: hiddenCount })}
                        </Button>
                      </div>
                    ) : null}
                  </div>
                );
              })}
            </div>
          </section>
        ))}
      </div>
    </div>
  );
}

/**
 * "2 hours ago" for anything recent, an absolute date once relative stops
 * meaning anything. Five identical timestamps down a column is noise; the exact
 * time stays available on hover via the `title` attribute.
 */
function formatRelativeNoteTime(iso: string): string {
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return "";
  const seconds = Math.round((Date.now() - then) / 1000);
  if (seconds < 60) return "just now";
  const minutes = Math.round(seconds / 60);
  if (minutes < 60)
    return `${minutes} ${minutes === 1 ? "minute" : "minutes"} ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours} ${hours === 1 ? "hour" : "hours"} ago`;
  const days = Math.round(hours / 24);
  if (days === 1) return "yesterday";
  if (days < 7) return `${days} days ago`;
  return formatPropertyNoteDate(iso);
}

/** Same date shape the notes panel uses, so a note reads identically in both. */
function formatPropertyNoteDate(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const months = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];
  const hours = String(d.getHours()).padStart(2, "0");
  const mins = String(d.getMinutes()).padStart(2, "0");
  return `${d.getDate()} ${months[d.getMonth()]} ${d.getFullYear()} · ${hours}:${mins}`;
}

type ActivityTimelineEntry = {
  id: string;
  timestamp: string;
  kind: "workflow_action" | "entity_log";
  label: string;
  description: string | null;
  actor: string;
  dotClass: string;
  badge: string | null;
  detail: ReactNode;
};

function PropertyActivityTab({
  holding,
  propertyHoldings,
}: {
  holding: Holding;
  propertyHoldings: Holding[];
}) {
  const { formatDate, formatTime } = useFormatting();

  const allWorkflowInstanceUuids = useMemo(() => {
    const uuids = new Set<string>();
    for (const h of propertyHoldings) {
      for (const ws of h.workflow_states ?? []) {
        if (ws.instance_uuid) uuids.add(ws.instance_uuid);
      }
    }
    return [...uuids];
  }, [propertyHoldings]);

  const primaryInstanceUuid = allWorkflowInstanceUuids[0] ?? null;
  const secondaryInstanceUuid = allWorkflowInstanceUuids[1] ?? null;
  const tertiaryInstanceUuid = allWorkflowInstanceUuids[2] ?? null;

  const { data: workflowLogs1 = [] } = useWorkflowHistory(primaryInstanceUuid);
  const { data: workflowLogs2 = [] } = useWorkflowHistory(
    secondaryInstanceUuid,
  );
  const { data: workflowLogs3 = [] } = useWorkflowHistory(tertiaryInstanceUuid);

  // Property changes (address, structure type, owner) are edited on the HOST
  // entity, customer edits on the CONSUMER entity, and warranty changes (tier
  // upgrades / downgrades, cancel, void) on the WARRANTY entity - all belong on
  // the registration's Activity trail. Aggregate the holding(s) + their shared
  // host + their consumers + their warranties so those changes are visible
  // chronologically here rather than vanishing under an entity the trail never
  // reads.
  const activityEntityIds = useMemo(() => {
    const ids = new Set<string>();
    for (const h of [...propertyHoldings, holding]) {
      if (h.uuid) ids.add(h.uuid);
      if (h.host?.uuid) ids.add(h.host.uuid);
      if (h.consumer_account?.uuid) ids.add(h.consumer_account.uuid);
      for (const w of h.warranties ?? []) {
        if (w.uuid) ids.add(w.uuid);
      }
    }
    return [...ids];
  }, [propertyHoldings, holding]);

  const { data: entityLogs = [], isLoading: entityLogsLoading } = useEntityLog({
    entity_key: "holding,host,consumer,warranty",
    entity_id: activityEntityIds.join(","),
    page_size: 200,
  });

  const allWorkflowLogs = useMemo(
    () => [...workflowLogs1, ...workflowLogs2, ...workflowLogs3],
    [workflowLogs1, workflowLogs2, workflowLogs3],
  );

  const timeline = useMemo(() => {
    const entries: ActivityTimelineEntry[] = [];

    const workflowCorrelationIds = new Set(
      entityLogs
        .filter((e) => e.event_type === "workflow_action")
        .map((e) => e.correlation_id)
        .filter(Boolean),
    );

    for (const log of allWorkflowLogs) {
      if (workflowCorrelationIds.has(log.correlation_id)) continue;

      const fromLabel = sentenceCaseKey(log.from_state_key || "");
      const toLabel = sentenceCaseKey(log.to_state_key || "");
      const transitionLabel =
        fromLabel && toLabel ? `${fromLabel} → ${toLabel}` : null;

      entries.push({
        id: `wf-${log.uuid}`,
        timestamp: log.created_at,
        kind: "workflow_action",
        label: sentenceCaseKey(log.action_key),
        description: transitionLabel,
        actor:
          log.initiated_by_name +
          (log.initiated_by_type === "SYSTEM" ? " (system)" : ""),
        dotClass: workflowDotClass(log.result),
        badge: log.result,
        detail: (
          <>
            {log.reason_text ? (
              <p className="mt-1.5 text-[12px] leading-relaxed text-[var(--ink-2)]">
                &ldquo;{log.reason_text}&rdquo;
              </p>
            ) : null}
            {log.error ? (
              <p className="mt-1 text-[12px] text-[var(--status-error)]">
                {log.error}
              </p>
            ) : null}
            {log.captured_values &&
            Object.keys(log.captured_values).length > 0 ? (
              <div className="mt-1.5 space-y-0.5">
                {Object.entries(log.captured_values)
                  .filter(
                    ([key, val]) =>
                      !INTERNAL_CAPTURED_VALUE_KEYS.has(key) &&
                      val != null &&
                      val !== "",
                  )
                  .map(([key, val]) => (
                    <p
                      key={key}
                      className="text-[12px] leading-relaxed text-[var(--ink-2)]"
                    >
                      <span className="font-medium">{sentenceCaseKey(key)}</span>
                      {": "}
                      <span>{formatLogValue(val)}</span>
                    </p>
                  ))}
              </div>
            ) : null}
          </>
        ),
      });
    }

    // A transfer logs its outcome on every registration it covers; the
    // property shows it once.
    const seenTransferEvents = new Set<string>();
    for (const entry of entityLogs) {
      const transferUuid = entry.metadata?.transfer_uuid;
      if (typeof transferUuid === "string" && transferUuid) {
        const key = `${entry.action_key}:${transferUuid}`;
        if (seenTransferEvents.has(key)) continue;
        seenTransferEvents.add(key);
      }
      const eventLabel =
        entry.action_label ||
        entry.action_key ||
        entry.event_type.replace(/_/g, " ");

      let detail: ReactNode = null;

      // A command action (a tier upgrade or downgrade, say) logs as an
      // ``entity_change`` with the same before/after + reason shape as a field
      // edit, so it gets the same detail - without it the Activity trail showed
      // only the action's name, not what changed or why.
      if (
        entry.event_type === "attribute_change" ||
        entry.event_type === "entity_change"
      ) {
        const oldVals = entry.old_values;
        const newVals = entry.new_values;
        const keys = [
          ...new Set([...Object.keys(oldVals), ...Object.keys(newVals)]),
        ];
        const changedKeys = keys.filter(
          (k) => formatLogValue(oldVals[k]) !== formatLogValue(newVals[k]),
        );
        const reason = entry.metadata?.reason;
        // An action that went through an exception names it: which exception
        // reason(s) were chosen, and the note when it says more than the reason.
        const exception = entry.metadata?.exception as
          | { reasons?: unknown; note?: unknown }
          | undefined;
        const exceptionReasons = Array.isArray(exception?.reasons)
          ? exception.reasons.filter(
              (r): r is string => typeof r === "string" && r.trim() !== "",
            )
          : [];
        const exceptionNote =
          typeof exception?.note === "string" &&
          exception.note.trim() &&
          exception.note.trim() !== String(reason ?? "").trim()
            ? exception.note.trim()
            : null;

        detail = (
          <>
            {typeof reason === "string" && reason.trim() ? (
              <p className="mt-1.5 text-[12px] leading-relaxed text-[var(--ink-2)]">
                Reason: <span className="font-medium">{reason}</span>
              </p>
            ) : null}
            {exception ? (
              <p className="mt-1 text-[12px] leading-relaxed text-[var(--ink-2)]">
                Exception applied:{" "}
                <span className="font-medium">
                  {exceptionReasons.join(", ") || "Yes"}
                </span>
                {exceptionNote ? ` - ${exceptionNote}` : null}
              </p>
            ) : null}
            {changedKeys.length > 0 ? (
              <div className="mt-1.5 space-y-0.5">
                {changedKeys.map((key) => {
                  const oldVal = formatLogValue(oldVals[key]);
                  const newVal = formatLogValue(newVals[key]);
                  return (
                    <p
                      key={key}
                      className="text-[12px] leading-relaxed text-[var(--ink-2)]"
                    >
                      <span className="font-medium">{sentenceCaseKey(key)}</span>
                      {": "}
                      {oldVal ? (
                        <>
                          <span className="text-[var(--ink-3)] line-through">
                            {oldVal}
                          </span>
                          {" → "}
                        </>
                      ) : null}
                      <span>{newVal || "-"}</span>
                    </p>
                  );
                })}
              </div>
            ) : null}
          </>
        );
      } else if (entry.event_type === "exception_granted") {
        const reasons = entry.new_values?.exception_reasons;
        const labels = Array.isArray(reasons)
          ? (reasons
              .map((r: unknown) => {
                if (
                  typeof r === "object" &&
                  r !== null &&
                  !Array.isArray(r) &&
                  typeof (r as Record<string, unknown>).title === "string"
                )
                  return (r as Record<string, string>).title;
                if (typeof r === "string") return sentenceCaseKey(r);
                return null;
              })
              .filter(Boolean) as string[])
          : [];

        detail =
          labels.length > 0 ? (
            <div className="mt-1.5 flex flex-wrap gap-1">
              {labels.map((lbl) => (
                <span
                  key={lbl}
                  className="inline-flex items-center rounded-full border-[0.5px] border-[var(--rule)] bg-[var(--bg-subtle)] px-2 py-0.5 text-[10.5px] text-[var(--ink-2)]"
                >
                  {lbl}
                </span>
              ))}
            </div>
          ) : null;
      } else if (entry.event_type === "workflow_issue") {
        const capturedValues = entry.new_values?.captured_values;
        const summary =
          typeof capturedValues === "object" &&
          capturedValues !== null &&
          !Array.isArray(capturedValues) &&
          typeof (capturedValues as Record<string, unknown>).summary ===
            "string"
            ? (capturedValues as Record<string, string>).summary.trim()
            : null;

        detail = (
          <>
            {summary ? (
              <p className="mt-1.5 text-[12px] leading-relaxed text-[var(--ink-2)]">
                Issue: <span className="font-medium">{summary}</span>
              </p>
            ) : null}
            {entry.comment ? (
              <p className="mt-1 text-[12px] leading-relaxed text-[var(--ink-2)]">
                {entry.comment}
              </p>
            ) : null}
          </>
        );
      } else if (entry.comment) {
        detail = (
          <p className="mt-1.5 text-[12px] leading-relaxed text-[var(--ink-2)]">
            {entry.comment}
          </p>
        );
      }

      entries.push({
        id: `el-${entry.uuid}`,
        timestamp: entry.occurred_at,
        kind: "entity_log",
        label: eventLabel,
        description: null,
        actor:
          entry.user_name ||
          entry.system_actor_key ||
          (entry.actor_type === "SYSTEM" ? "System" : "User"),
        dotClass: entityDotClass(entry.event_type),
        badge: entry.event_type.replace(/_/g, " "),
        detail,
      });
    }

    entries.sort(
      (a, b) =>
        new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime(),
    );

    return entries;
  }, [allWorkflowLogs, entityLogs]);

  if (entityLogsLoading) {
    return (
      <div className="mt-8 rounded-[6px] border-[0.5px] border-[var(--rule-light)] px-5 py-[60px] text-center text-[13px] text-[var(--ink-3)]">
        Loading activity...
      </div>
    );
  }

  if (timeline.length === 0) {
    return (
      <div className="mt-8 rounded-[6px] border-[0.5px] border-[var(--rule-light)] px-5 py-[60px] text-center text-[13px] text-[var(--ink-3)]">
        <span className="font-display text-[22px] font-light text-[var(--ink-2)]">
          Activity
        </span>
        <div className="mt-1.5">No activity recorded yet.</div>
      </div>
    );
  }

  return (
    <div className="mt-8">
      <div className="mb-5 flex items-center justify-between">
        <div>
          <div className="text-[10px] font-normal uppercase tracking-[0.16em] text-[var(--ink-3)]">
            Timeline
          </div>
          <h3 className="mt-1 text-[15px] text-[var(--ink)]">All activity</h3>
        </div>
        <span className="text-[12px] text-[var(--ink-3)]">
          {timeline.length} {timeline.length === 1 ? "event" : "events"}
        </span>
      </div>

      <div className="relative">
        <span className="absolute bottom-0 left-[7px] top-0 w-px bg-[var(--rule)]" />
        <div className="flex flex-col gap-6">
          {timeline.map((entry) => {
            const formattedDate = formatDate(entry.timestamp);
            const formattedTime = formatTime(entry.timestamp);

            return (
              <div
                key={entry.id}
                className="relative grid grid-cols-[16px_1fr] gap-4"
              >
                <span
                  className={cn(
                    "relative z-10 mt-1 h-4 w-4 rounded-full border-2 border-[var(--bg)] shadow-[0_0_0_0.5px_var(--rule)]",
                    entry.dotClass,
                  )}
                />
                <div className="min-w-0 pb-1">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="text-[13px] font-medium leading-[1.35] text-[var(--ink)]">
                        {entry.label}
                      </p>
                      {entry.description ? (
                        <p className="mt-0.5 text-[12px] text-[var(--ink-2)]">
                          {entry.description}
                        </p>
                      ) : null}
                      <p className="mt-1 text-[11px] text-[var(--ink-3)]">
                        {entry.actor} · {formattedDate} at {formattedTime}
                      </p>
                    </div>
                    {entry.badge ? (
                      <span className="shrink-0 rounded-full border-[0.5px] border-[var(--rule)] bg-[var(--bg-subtle)] px-2 py-0.5 text-[10px] uppercase tracking-[0.06em] text-[var(--ink-3)]">
                        {entry.badge}
                      </span>
                    ) : null}
                  </div>
                  {entry.detail}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}


function formatLogValue(value: unknown): string {
  if (value == null || value === "") return "";
  if (typeof value === "boolean") return value ? "Yes" : "No";
  // Composite ADDRESS values (e.g. a mailing-address change) are stored as a
  // structured object. Render the human-readable address rather than letting
  // it fall through to String(value) -> "[object Object]", which both hides the
  // real value and collapses the old -> new diff.
  if (isAddressLike(value)) return addressDisplay(value);
  return String(value);
}

function workflowDotClass(result: WorkflowActionLog["result"]): string {
  if (result === "OK") return "bg-[var(--green)]";
  if (result === "FAILED") return "bg-[var(--status-error)]";
  if (result === "PENDING_APPROVAL") return "bg-[var(--gold)]";
  return "bg-[var(--ink-3)]";
}

function entityDotClass(eventType: string): string {
  if (eventType === "exception_granted") return "bg-[var(--green)]";
  if (eventType === "workflow_issue") return "bg-[var(--gold)]";
  if (eventType === "attribute_change") return "bg-[var(--ink-2)]";
  if (eventType === "workflow_action") return "bg-[var(--green)]";
  return "bg-[var(--ink-3)]";
}

function PropertyRecordSummaryField({
  label,
  children,
}: {
  label: string;
  children: ReactNode;
}) {
  return (
    <div className="min-w-0">
      <div className="mb-2 text-[10px] font-normal uppercase tracking-[0.16em] text-[var(--ink-3)]">
        {label}
      </div>
      {children}
    </div>
  );
}

function PropertyToolbarButton({
  icon,
  children,
}: {
  icon: ReactNode;
  children: ReactNode;
}) {
  return (
    <Button
      type="button"
      variant="outline"
      className="h-8 rounded-[4px] border-[var(--rule)] bg-transparent px-2.5 text-[10.5px] font-normal uppercase tracking-[0.12em] text-[var(--ink-3)]"
    >
      {icon}
      {children}
    </Button>
  );
}

function PropertyRecordHeaderCell({
  children,
  align = "left",
}: {
  children?: ReactNode;
  align?: "left" | "right";
}) {
  return (
    <div
      className={`px-3.5 py-2.5 text-[10.5px] font-normal uppercase tracking-[0.16em] text-[var(--ink-3)] ${
        align === "right" ? "text-right" : "text-left"
      }`}
    >
      {children}
    </div>
  );
}

function PropertyRecordProductCell({
  row,
}: {
  row: Pick<
    PropertyRecordWarrantyRow,
    "product" | "variant" | "serialNumber" | "serialLabel"
  >;
}) {
  return (
    <>
      <div className="text-[13px] font-light text-[var(--ink)]">
        {row.product}
      </div>
      <div className="mt-0.5 text-[11.5px] font-light text-[var(--ink-3)]">
        {row.variant}
      </div>
      {row.serialNumber ? (
        <div
          data-testid="property-registration-serial"
          className="mt-0.5 font-mono text-[11px] text-[var(--ink-3)]"
        >
          {row.serialLabel
            ? `${row.serialLabel} ${row.serialNumber}`
            : row.serialNumber}
        </div>
      ) : null}
    </>
  );
}

function PropertyRecordCell({
  children,
  align = "left",
  muted = false,
  mono = false,
}: {
  children: ReactNode;
  align?: "left" | "right";
  muted?: boolean;
  mono?: boolean;
}) {
  return (
    <div
      className={`px-3.5 py-2.5 text-[13px] font-light ${
        align === "right" ? "text-right" : "text-left"
      } ${muted ? "text-[var(--ink-3)]" : "text-[var(--ink)]"} ${
        mono ? "font-mono" : ""
      }`}
    >
      {children}
    </div>
  );
}

function PropertyRecordPill({
  tone,
  children,
}: {
  tone: "ok" | "warn" | "neutral";
  children: ReactNode;
}) {
  const classes =
    tone === "warn"
      ? "border-[var(--gold-border)] bg-[var(--gold-bg)] text-[var(--gold-ink)]"
      : tone === "neutral"
        ? "border-[var(--rule)] bg-[var(--bg-subtle)] text-[var(--ink-3)]"
        : "border-[rgba(28,90,72,0.2)] bg-[var(--status-ok-bg)] text-[var(--status-ok)]";
  return (
    <span
      className={`inline-flex h-[22px] items-center gap-1.5 rounded-full border-[0.5px] px-2.5 text-[10.5px] font-normal uppercase tracking-[0.08em] ${classes}`}
    >
      {children}
    </span>
  );
}

function propertyRecordStatusTone(
  statusKey: string,
): "ok" | "warn" | "neutral" {
  if (statusKey === "active" || statusKey === "approved") return "ok";
  if (
    statusKey === "draft" ||
    statusKey === "pending" ||
    statusKey === "pending_approval" ||
    statusKey === "needs_action"
  ) {
    return "warn";
  }
  return "neutral";
}

function DetailSection({
  label,
  children,
}: {
  label: string;
  children: ReactNode;
}) {
  return (
    <section className="flex flex-col gap-3">
      <div className="flex items-center gap-3">
        <span className="shrink-0 text-[11px] font-normal uppercase tracking-[0.14em] text-[var(--ink-3)]">
          {label}
        </span>
        <div className="h-px flex-1 bg-[var(--rule)]" />
      </div>
      {children}
    </section>
  );
}

function WarrantySummaryBody({
  warranties,
}: {
  warranties: Array<HoldingWarranty | PendingWarrantySummary>;
}) {
  const { t } = useTranslation();
  const { formatDate } = useFormatting();
  if (warranties.length === 0) {
    return (
      <SummaryDetailGrid>
        <SummaryDetailItem label="Class" value={undefined} />
        <SummaryDetailItem label="Duration" value={undefined} />
        <SummaryDetailItem label="Status" value={undefined} />
      </SummaryDetailGrid>
    );
  }

  return (
    <div className="space-y-5">
      {warranties.map((w, index) => {
        const typeLabel =
          WARRANTY_TYPE_LABELS[w.warranty_type] ?? w.warranty_type;
        const coverageLabel = w.coverage_scope
          ? (COVERAGE_LABELS[w.coverage_scope] ?? w.coverage_scope)
          : null;
        const statusLabel = w.status.includes(".")
          ? w.status.split(".").pop()!
          : w.status;
        const displayStatus =
          statusLabel.charAt(0).toUpperCase() +
          statusLabel.slice(1).toLowerCase();
        const duration = w.is_lifetime
          ? t("registrations.warrantyLifetime")
          : w.duration_in_months
            ? formatDurationFromMonths(w.duration_in_months)
            : "-";
        const attributeRows = (w.attribute_values ?? []).filter(
          (av) =>
            av.value !== null && av.value !== undefined && av.value !== "",
        );
        const documents = "documents" in w ? (w.documents ?? []) : [];

        return (
          <div
            key={w.uuid}
            className={
              index > 0 ? "border-t-[0.5px] border-t-[var(--rule)] pt-5" : ""
            }
          >
            {warranties.length > 1 && (
              <div className="mb-4">
                <div className="text-[11px] font-normal uppercase tracking-[0.14em] text-[var(--ink-3)]">
                  Warranty {index + 1}
                </div>
                <div className="mt-1 text-[16px] font-light text-[var(--ink)]">
                  {w.class_name || typeLabel}
                </div>
              </div>
            )}
            <SummaryDetailGrid>
              <SummaryDetailItem label="Status" value={displayStatus} />
              <SummaryDetailItem label="Type" value={typeLabel} />
              <SummaryDetailItem label="Class" value={w.class_name} />
              <SummaryDetailItem label="Duration" value={duration} />
              <SummaryDetailItem
                label="Active from"
                value={formatDate(w.active_from)}
              />
              {w.expires_at && (
                <SummaryDetailItem
                  label="Expires"
                  value={formatDate(w.expires_at)}
                />
              )}
              {coverageLabel && (
                <SummaryDetailItem label="Coverage" value={coverageLabel} />
              )}
              {attributeRows.map((av) => (
                <SummaryDetailItem
                  key={av.code_name}
                  label={av.display_name}
                  value={formatAttrValue(av.value)}
                />
              ))}
            </SummaryDetailGrid>
            {documents.length > 0 && (
              <div className="mt-4 border-t-[0.5px] border-t-[var(--rule)] pt-4">
                <div className="mb-2 text-[11px] font-normal uppercase tracking-[0.14em] text-[var(--ink-3)]">
                  Documents
                </div>
                <div className="flex flex-col gap-1.5">
                  {documents.map((d) => (
                    <a
                      key={d.uuid}
                      href={d.url ?? "#"}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1.5 text-[12px] font-light text-[var(--green)] hover:underline"
                    >
                      <ExternalLink className="h-3 w-3" />
                      {d.label}
                    </a>
                  ))}
                </div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

/**
 * DraftResolutionPage
 *
 * Wraps :class:`RegistrationResolutionWorkspace` in the in-page
 * layout (no dialog chrome). Loads the holding's claims and shapes
 * them for the workspace's ``claims`` prop - identical logic to the
 * dialog wrapper, so clicking "Draft" from the registrations list
 * renders the same triage canvas reviewers see from the
 * exception-requests entry.
 */
function DraftResolutionPage({
  holdingUuid,
  productLabel,
  canApprove,
  onBack,
}: {
  holdingUuid: string;
  productLabel: string;
  canApprove: boolean;
  onBack: () => void;
}) {
  const { locale } = useFormatting();
  const { data: claimsData } = useClaims({ holding_uuid: holdingUuid });
  const claims = (claimsData?.results ?? []).map((c) => {
    const status = (c.status ?? "").toLowerCase();
    const statusVariant: "open" | "paid" | "closed" =
      status.includes("open") ||
      status.includes("review") ||
      status.includes("pending")
        ? "open"
        : status.includes("paid") ||
            status.includes("settled") ||
            status.includes("approved")
          ? "paid"
          : "closed";
    const raised =
      (c as { raised_at?: string | null; created_at?: string | null })
        .raised_at ??
      (c as { created_at?: string | null }).created_at ??
      "";
    return {
      uuid: c.uuid,
      raisedOn: raised
        ? new Date(raised).toLocaleDateString(locale, {
            day: "2-digit",
            month: "short",
            year: "2-digit",
          })
        : "-",
      summary:
        (c as { summary?: string; cause?: string }).summary ??
        (c as { cause?: string }).cause ??
        "Claim",
      statusLabel:
        statusVariant === "open"
          ? "Open"
          : statusVariant === "paid"
            ? "Paid"
            : "Closed",
      statusVariant,
    };
  });

  return (
    <div className="min-h-[calc(100vh-64px)] bg-[var(--bg)]">
      <RegistrationResolutionWorkspace
        holdingUuid={holdingUuid}
        canApprove={canApprove}
        warrantyIntentLabel={productLabel}
        claims={claims}
        onClose={onBack}
        backLabel="Back to registrations"
      />
    </div>
  );
}
